import React from 'react';
import { api, ApiError, fmt } from '../api';
import { Empty, ErrorBanner, Field, Hero, Loading, Pill, SegmentedTabs, statusTone, timezoneSelectOptions, TrustStrip, useAsync } from '../ui';
import { navigate, type Me } from '../App';
import { FamilyPermissions } from './Family';
import { useDraftProtection } from '../navigation';
import { canvasProfileSettingsUrl } from '../canvas-settings';
import {
  assistantReadiness, childLocation, hasFamilyPermissions, initialOnboarding, nextSetupGate,
} from '../student-journey';
import { calendarConnectionLabel, calendarEmptyPreviewMessage, calendarLinkValidationError, classEventSeries, selectedClassEvents } from '../class-schedule-ui';
import { TutorialVideo } from '../TutorialVideo';
import {
  BackpackIllustration, CalendarStarIllustration, ChatBubbleIllustration, NotebookIllustration,
  PaperAirplaneIllustration, ShieldHeartIllustration,
} from '../illustrations';
const DEFAULT_SPORT_PACKING: Record<string, string[]> = {
  soccer: ['cleats', 'shin guards', 'water bottle', 'uniform'],
  basketball: ['basketball shoes', 'water bottle', 'uniform'],
  football: ['cleats', 'mouthguard', 'water bottle', 'uniform'],
  volleyball: ['court shoes', 'knee pads', 'water bottle', 'uniform'],
  baseball: ['glove', 'hat', 'water bottle', 'uniform'],
  softball: ['glove', 'hat', 'water bottle', 'uniform'],
  swimming: ['swimsuit', 'goggles', 'towel', 'water bottle'],
  track: ['running shoes', 'uniform', 'water bottle'],
  'cross country': ['running shoes', 'water bottle'],
  tennis: ['racket', 'tennis shoes', 'water bottle'],
  golf: ['clubs', 'golf shoes', 'water bottle'],
  lacrosse: ['stick', 'cleats', 'mouthguard', 'water bottle'],
  'field hockey': ['stick', 'shin guards', 'mouthguard', 'water bottle'],
  cheer: ['uniform', 'shoes', 'water bottle'],
  gymnastics: ['leotard', 'grips', 'water bottle'],
  wrestling: ['headgear', 'wrestling shoes', 'water bottle'],
};

// Common sport choices for the setup dropdown, plus an "Other" escape hatch
// for anything not listed (parent/student types the name in free text).
const SPORT_OPTIONS = [
  { value: '', label: 'Choose a sport…' },
  ...Object.keys(DEFAULT_SPORT_PACKING).map((s) => ({
    value: s,
    label: s.replace(/\b\w/g, (c) => c.toUpperCase()),
  })),
  { value: '__other__', label: 'Other (type it in)' },
];

function inferCanvasBaseUrlFromSchoolName(value: string): string | null {
  const school = value.trim().toLowerCase();
  if (!school) return null;
  if (/^https?:\/\//i.test(school)) {
    try {
      const parsed = new URL(school);
      if (parsed.protocol === 'https:') return `${parsed.origin}`;
    } catch {
      return null;
    }
  }
  if (school.includes('bush')) return 'https://bush.instructure.com';
  return null;
}

function looksLikeEmail(value: string): boolean {
  const trimmed = value.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

type Ctx = { studentId: string; reload: () => void; data: any };

function useAction(reload: () => void) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [note, setNote] = React.useState<string | null>(null);
  const run = async (fn: () => Promise<any>, successNote?: string) => {
    setBusy(true); setError(null); setNote(null);
    try {
      const res = await fn();
      if (res?.ok === false && res.blockers) setError(`Blocked: ${res.blockers.join(' · ')}`);
      else if (res?.ok === false) setError('That did not work. Check the code or details, then try again.');
      else if (successNote) setNote(successNote);
      reload();
      return res;
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, note, run, setError, setNote };
}

function Notices({ a }: { a: ReturnType<typeof useAction> }) {
  // Errors/success notes can render far below the action that triggered them on long
  // pages (e.g. Configure assistant). Auto-scroll and move focus so the message is never
  // missed, instead of relying on the parent to keep this near every possible trigger.
  const ref = React.useRef<HTMLParagraphElement | null>(null);
  React.useEffect(() => {
    if ((a.error || a.note) && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      ref.current.focus();
    }
  }, [a.error, a.note]);
  return (
    <>
      {a.error && <p ref={ref} className="banner bad" role="alert" tabIndex={-1}>{a.error}</p>}
      {a.note && <p ref={ref} className="banner ok" role="status" tabIndex={-1}>{a.note}</p>}
    </>
  );
}

function primarySmsContact(data: any) {
  return (data.smsContacts ?? []).find((c: any) =>
    c.isPrimary && c.verifiedAt && c.consentAt && !c.optedOutAt && c.status === 'subscribed')
    ?? (data.smsContacts ?? []).find((c: any) => c.isPrimary)
    ?? (data.smsContacts ?? []).find((c: any) =>
    c.verifiedAt && c.consentAt && !c.optedOutAt && c.status === 'subscribed')
    ?? (data.smsContacts ?? [])[0];
}

function phoneVerificationErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'phone_already_verified_for_another_student') return error.message;
    if (error.code === 'too_many_attempts') return 'Too many incorrect attempts. Send a new verification code, then try again.';
    if (error.code === 'not_found') return 'This verification request is no longer available. Send a new code to the current student phone.';
    return error.message;
  }
  return error instanceof Error ? error.message : 'Could not verify that code. Send a new code or try again.';
}

function setupState(data: any, me: Me) {
  const sms = primarySmsContact(data);
  const phoneVerified = data.readiness?.smsDestinationReady === true;
  const assignmentSourceReady = data.readiness?.assignmentSource?.ready === true;
  const emailReady = Boolean(data.student.student_email_alias && data.student.student_email_alias_verified);
  const permissionsReady = hasFamilyPermissions(me);
  const workerActive = data.worker?.status === 'active';
  const complete = permissionsReady && assignmentSourceReady && phoneVerified && workerActive;
  return { sms, phoneVerified, assignmentSourceReady, emailReady, permissionsReady, workerActive, complete };
}

function repairReadiness(data: any, me: Me) {
  return assistantReadiness({
    worker: data.worker,
    readiness: data.readiness,
    smsContacts: data.smsContacts,
    voiceTrustedUntil: data.voiceTrustedUntil,
    permissionsReady: hasFamilyPermissions(me),
  });
}

type OptionalOnboardingStep = 'class_calendar' | 'sports' | 'assistant_settings';
type OptionalOnboardingStatus = 'skipped' | 'configured' | 'customized' | 'defaults_retained';

function optionalStepStatus(data: any, step: OptionalOnboardingStep): OptionalOnboardingStatus | 'not_started' {
  const stored = data.optionalSetup?.state?.[step] as OptionalOnboardingStatus | undefined;
  if (step === 'class_calendar') {
    if (data.optionalSetup?.classCalendar?.configured) return 'configured';
    return stored ?? 'not_started';
  }
  if (step === 'sports') {
    const teams = Number(data.optionalSetup?.sports?.team_count ?? 0);
    if (teams > 0) return 'configured';
    return stored ?? 'not_started';
  }
  return stored ?? 'not_started';
}

function optionalStatusLabel(status: ReturnType<typeof optionalStepStatus>): string {
  if (status === 'configured') return 'Configured';
  if (status === 'skipped') return 'Skipped for now';
  if (status === 'customized') return 'Customized';
  if (status === 'defaults_retained') return 'Defaults kept';
  return 'Not started';
}

function allOptionalOnboardingComplete(data: any): boolean {
  return (['class_calendar', 'sports', 'assistant_settings'] as OptionalOnboardingStep[])
    .every((step) => optionalStepStatus(data, step) !== 'not_started');
}

/**
 * Core setup gates are distinct from optional email and sports features.
 */
function SetupSummary({ ctx, me, onGoTo, onChanged }: { ctx: Ctx; me: Me; onGoTo: (tab: string, anchor?: string) => void; onChanged: () => void | Promise<void> }) {
  const { data, studentId, reload } = ctx;
  const s = data.student;
  const state = setupState(data, me);
  const a = useAction(reload);
  const [expandedOptional, setExpandedOptional] = React.useState<OptionalOnboardingStep | null>(null);
  const [advanceAfterPermissions, setAdvanceAfterPermissions] = React.useState(false);
  const canvasConnection = data.connections?.find((connection: any) => connection.provider === 'canvas');
  const optionalState = {
    class_calendar: optionalStepStatus(data, 'class_calendar'),
    sports: optionalStepStatus(data, 'sports'),
    assistant_settings: optionalStepStatus(data, 'assistant_settings'),
  };
  const repair = repairReadiness(data, me);
  const markOptional = (step: OptionalOnboardingStep, status: OptionalOnboardingStatus) =>
    a.run(() => api('PATCH', `/api/students/${studentId}/onboarding`, { step, status }), 'Progress saved.')
      .then((res) => { if (res?.ok) setExpandedOptional(null); });
  const schoolDetail = state.assignmentSourceReady
    ? 'Canvas is connected and its first successful assignment sync is complete.'
    : state.workerActive
      ? canvasConnection?.last_sync_status === 'failed'
        ? 'This assistant is already active, but its Canvas sync failed. Retry the sync to restore current assignment updates.'
        : 'This assistant is already active. Connect Canvas for assignment updates; a successful first sync will be required before any future activation.'
    : canvasConnection?.last_sync_status === 'failed'
      ? 'Canvas is connected, but its first assignment sync failed. Retry the sync in Connect school.'
      : canvasConnection
      ? 'Canvas is connected and the first sync starts automatically. Most families see it complete in about 1-2 minutes.'
        : 'Connect the student’s Canvas account and complete the first sync before activation. No assignments need to exist yet.';

  const steps: Array<{ key: string; title: string; done: boolean; detail: string; action: string; tab: string | null; anchor?: string }> = [
    {
      key: 'permissions',
      title: 'Permissions',
      done: state.permissionsReady,
      detail: state.permissionsReady
        ? 'Recorded for your family — covers school data storage, SMS messaging, AI interpretation, and voice conversations when enabled.'
        : 'Authorize school data storage, SMS messaging, AI interpretation, and voice conversations when enabled. Required before anything else can be set up.',
      action: state.permissionsReady ? 'Review permissions' : 'Open permissions',
      tab: 'family',
    },
    {
      key: 'profile',
      title: 'Child profile',
      done: true,
      detail: `${s.preferred_name}’s profile is saved. You can update profile details later in settings.`,
      action: 'Review profile',
      tab: 'settings',
      anchor: 'student-settings',
    },
    {
      key: 'school',
      title: 'Connect Canvas homework',
      done: state.assignmentSourceReady,
      detail: schoolDetail,
      action: state.assignmentSourceReady ? 'Review school connection' : 'Connect Canvas',
      tab: 'school',
    },
    {
      key: 'phone',
      title: 'Verify student phone',
      done: state.phoneVerified,
      detail: state.phoneVerified
        ? `${state.sms?.destination ?? 'The student phone'} is verified and can receive reminders.`
        : 'Send a code to the phone that will receive reminders and can text the assistant back, then confirm it.',
      action: state.phoneVerified ? 'Review phone' : 'Verify phone',
      tab: 'settings',
      anchor: 'phone-verification-section',
    },
    {
      key: 'activate',
      title: 'Activate assistant',
      done: state.workerActive,
      detail: state.workerActive
        ? 'The assistant is active.'
        : 'Turn the assistant on after permissions, Canvas sync, and phone verification are complete.',
      action: state.workerActive ? 'Manage assistant' : 'Activate assistant',
      tab: null,
    },
  ];

  const gate = nextSetupGate(state, data.readiness?.ready === true);
  const requiredNextIndex = repair.phoneRepairNeeded ? steps.findIndex((step) => step.key === 'phone')
    : state.workerActive ? -1 : steps.findIndex((step) => step.key === gate);
  const optionalSteps = [
    {
      key: 'class_calendar' as const,
      title: 'Class calendar setup',
      status: optionalState.class_calendar,
      detail: 'Connect a class calendar to answer class-time and room questions and to enable class schedule reminders.',
      unavailable: 'If skipped, assignment and SMS features still work, but class-time questions, class rooms, class reminders, and class-to-game timing stay unavailable until a calendar is connected.',
      tab: 'class-schedule',
    },
    {
      key: 'sports' as const,
      title: 'Sports schedules and packing-list reminders',
      status: optionalState.sports,
      detail: 'Add sports teams so the assistant can answer game schedule questions, use packing lists, and manage game-day reminders.',
      unavailable: 'If skipped, schoolwork still works, but sports schedules, game-day packing help, and sports reminders stay unavailable until teams are added.',
      tab: 'sports',
    },
    {
      key: 'assistant_settings' as const,
      title: 'Assistant settings',
      status: optionalState.assistant_settings,
      detail: 'Review reminder times, quiet hours, daily message limits, timezone, and what message history parents can see.',
      unavailable: 'Defaults are safe to start with and can be changed later in Configuration & Tools.',
      tab: 'settings',
    },
  ];
  const nextOptionalIndex = state.workerActive && !repair.phoneRepairNeeded
    ? optionalSteps.findIndex((step) => step.status === 'not_started')
    : -1;
  const complete = !repair.phoneRepairNeeded && requiredNextIndex === -1 && nextOptionalIndex === -1;
  React.useEffect(() => {
    if (!advanceAfterPermissions || !state.permissionsReady) return;
    const target = document.querySelector('.setup-step-next');
    if (target instanceof HTMLElement) {
      target.scrollIntoView({ behavior: 'auto', block: 'center' });
      target.focus({ preventScroll: true });
    }
    setAdvanceAfterPermissions(false);
  }, [advanceAfterPermissions, state.permissionsReady, requiredNextIndex]);
  const classConnection = data.optionalSetup?.classCalendar?.connection;
  const classEventCount = Number(classConnection?.event_count ?? 0);
  const classStatusText = classConnection
    ? !classConnection.last_sync_at && !classConnection.last_sync_status
      ? 'Connected, awaiting first sync'
      : classConnection.last_sync_status === 'failed'
        ? 'Connected, last sync failed'
        : classConnection.last_sync_status === 'success' && classEventCount === 0
          ? 'Connected, synced successfully with 0 class events imported'
          : classConnection.last_sync_status === 'success'
            ? `${classEventCount} scheduled class event${classEventCount === 1 ? '' : 's'} imported`
            : `Connected, sync status: ${fmt.label(classConnection.last_sync_status ?? 'not synced')}`
    : null;
  const sportsSummary = data.optionalSetup?.sports ?? {};
  const sportsTeamCount = Number(sportsSummary.team_count ?? 0);
  const sportsGameCount = Number(sportsSummary.upcoming_game_count ?? 0);
  const notificationsEnabledCount = Number(sportsSummary.notifications_enabled_count ?? 0);
  const smsHref = data.smsSenderAddress
    ? `sms:${encodeURIComponent(data.smsSenderAddress)}?&body=${encodeURIComponent('plan week')}`
    : null;
  const recommendedNextSetup = [
    optionalState.class_calendar !== 'configured' && {
      key: 'class-schedule',
      title: 'Set up class schedule',
      detail: 'For class times, rooms, and “Where is History?”',
      tab: 'class-schedule',
    },
    optionalState.sports !== 'configured' && {
      key: 'sports',
      title: 'Set up sports',
      detail: 'For games, packing, and class-to-game timing.',
      tab: 'sports',
    },
    !state.emailReady && {
      key: 'email',
      title: 'Set up email forwarding',
      detail: 'For turning school emails into reviewable activities.',
      tab: 'email',
    },
  ].filter(Boolean) as Array<{ key: string; title: string; detail: string; tab: string }>;

  return (
    <section className="card" id="setup-summary">
      <div className="spread">
        <div>
          <h2>Guided setup for {s.preferred_name}</h2>
          <p className="muted" style={{ margin: 0 }}>
            Complete one required step at a time. Completed progress is saved. Family permissions apply to every child;
            Canvas and phone verification belong to {s.preferred_name} only.
          </p>
        </div>
        <Pill tone={state.workerActive || complete ? 'ok' : 'warn'}>
          {complete ? 'Setup complete' : state.workerActive ? 'Assistant active' : 'Setup not complete'}
        </Pill>
      </div>
      {repair.phoneRepairNeeded && (
        <div className="banner bad" role="alert" style={{ marginTop: 12 }}>
          <strong>Repair needed: SMS is not ready</strong>
          <p>
            The assistant is enabled, but reminders and assistant texts cannot send until the student phone is verified,
            consented, subscribed, and not opted out.
          </p>
          <button type="button" className="primary" onClick={() => onGoTo('phone', 'phone-verification-section')}>
            Verify student phone
          </button>
        </div>
      )}

      <ol className="setup-steps">
        {steps.map((step, i) => {
          const status = step.done ? 'done' : i === requiredNextIndex ? 'next' : 'todo';
          return (
            <li
              key={step.key}
              id={`setup-step-${step.key}`}
              className={`setup-step setup-step-${status}`}
              tabIndex={status === 'next' ? -1 : undefined}
            >
              <div className="setup-step-badge" aria-hidden="true">{step.done ? '✓' : i + 1}</div>
              <div className="setup-step-body">
                <div className="spread">
                  <strong>{step.title}</strong>
                  {status === 'done' && <Pill tone="ok">Completed</Pill>}
                  {status === 'next' && <Pill tone="warn">Next step</Pill>}
                </div>
                {status === 'next' && <p className="muted" style={{ margin: '4px 0 8px' }}>{step.detail}</p>}
                {status === 'todo' && (
                  <p className="field-help" style={{ marginTop: 0 }}>
                    Comes after {steps[i - 1]?.title.toLowerCase()}.
                  </p>
                )}
                {step.key === 'activate' && status === 'next' ? (
                  <button
                    type="button"
                    className={status === 'next' ? 'primary' : ''}
                    disabled={a.busy || data.readiness?.ready !== true}
                    onClick={() => {
                      a.run(
                        () => api('POST', `/api/students/${studentId}/worker/status`, { status: 'active' }),
                        `The assistant is active. Continue with the optional setup steps below, or return later from this guided setup page.`,
                      );
                    }}
                  >
                    {a.busy ? 'Activating…' : step.done ? 'Manage assistant' : i !== requiredNextIndex ? 'Finish earlier steps first' : 'Activate assistant'}
                  </button>
                ) : null}
                {status === 'next' && step.key === 'permissions' && (
                  <div className="setup-step-panel">
                    <FamilyPermissions me={me} onChanged={onChanged} onReady={() => setAdvanceAfterPermissions(true)} />
                  </div>
                )}
                {status === 'next' && step.key === 'school' && (
                  <div className="setup-step-panel"><School ctx={ctx} guided /></div>
                )}
                {status === 'next' && step.key === 'phone' && (
                  <div className="setup-step-panel"><Settings ctx={ctx} canEdit section="phone" /></div>
                )}
              </div>
            </li>
          );
        })}
        {optionalSteps.map((step, index) => {
          const globalIndex = steps.length + index;
          const isNext = index === nextOptionalIndex;
          const isOpen = state.workerActive && (expandedOptional === step.key || isNext);
          const done = state.workerActive && step.status !== 'not_started';
          return (
            <li key={step.key} className={`setup-step setup-step-${done ? 'done' : isNext ? 'next' : 'todo'}`}>
              <div className="setup-step-badge" aria-hidden="true">{done ? '✓' : globalIndex + 1}</div>
              <div className="setup-step-body">
                <div className="spread">
                  <strong>{`Step ${globalIndex + 1}: ${step.title}`}</strong>
                  <Pill tone={done ? step.status === 'skipped' || step.status === 'defaults_retained' ? 'warn' : 'ok' : isNext ? 'warn' : undefined}>
                    {optionalStatusLabel(step.status)}
                  </Pill>
                </div>
                <p className={isNext ? 'muted' : 'field-help'} style={{ margin: '4px 0 8px' }}>{step.detail}</p>
                <p className="field-help">{step.unavailable}</p>
                {done && !isOpen && (
                  <div className="row" style={{ marginTop: 8 }}>
                    <button type="button" onClick={() => setExpandedOptional(step.key)}>
                      {step.status === 'skipped' ? 'Set up now' : 'Review or change'}
                    </button>
                    <button type="button" onClick={() => onGoTo(step.tab)}>Open in Configuration & Tools</button>
                  </div>
                )}
                {!done && !isOpen && (
                  <p className="field-help">
                    {state.workerActive
                      ? `Comes after ${optionalSteps[index - 1]?.title.toLowerCase() ?? 'activation'}.`
                      : 'Comes after activation.'}
                  </p>
                )}
                {isOpen && (
                  <div className="setup-step-panel">
                    {step.key === 'class_calendar' && (
                      <>
                        <ClassSchedule
                          ctx={ctx}
                          canEdit
                          onSetUpLater={() => markOptional('class_calendar', 'skipped')}
                        />
                        {data.optionalSetup?.classCalendar?.configured && (
                          <div className="row" style={{ marginTop: 10 }}>
                            <button type="button" className="primary" onClick={() => setExpandedOptional(null)}>Continue</button>
                          </div>
                        )}
                      </>
                    )}
                    {step.key === 'sports' && (
                      <>
                        <SportsSetup ctx={ctx} />
                        {Number(data.optionalSetup?.sports?.team_count ?? 0) > 0 && (
                          <div className="row" style={{ marginTop: 10 }}>
                            <button type="button" className="primary" onClick={() => setExpandedOptional(null)}>Continue</button>
                          </div>
                        )}
                        {Number(data.optionalSetup?.sports?.team_count ?? 0) === 0 && (
                          <div className="row" style={{ marginTop: 10 }}>
                            <button type="button" disabled={a.busy} onClick={() => markOptional('sports', 'skipped')}>Skip for now</button>
                          </div>
                        )}
                      </>
                    )}
                    {step.key === 'assistant_settings' && (
                      <>
                        <div className={state.phoneVerified ? 'banner ok' : 'banner'}>
                          <strong>Voice calls</strong>
                          {state.phoneVerified && data.smsSenderAddress ? (
                            <p>
                              Voice is available from the verified student phone. {s.preferred_name} can call{' '}
                              <span className="mono">{data.smsSenderAddress}</span>, the same number used for assistant texts.
                            </p>
                          ) : state.phoneVerified ? (
                            <p>Voice uses the verified student phone. The assistant number is managed by the configured messaging service.</p>
                          ) : (
                            <p>Voice calls need the student phone to be verified first. You can finish phone verification in Step 4 or Student phone settings.</p>
                          )}
                        </div>
                        <div className="banner">
                          <strong>Current defaults</strong>
                          <ul>
                            <li>Timezone: {s.timezone}</li>
                            <li>Leaves for school: {fmt.clockTime(s.school_departure_time)}</li>
                            <li>After-school planning: {fmt.clockTime(s.after_school_plan_time)}</li>
                            <li>Evening packing reminder: {fmt.clockTime(s.evening_pack_time)}</li>
                            <li>Quiet hours: {fmt.clockTime(s.quiet_hours_start)} to {fmt.clockTime(s.quiet_hours_end)}</li>
                            <li>Daily message limit: {s.daily_message_limit}</li>
                            <li>Parent visibility: {VISIBILITY_LABEL[s.parent_visibility ?? 'summary'] ?? 'activity and task data'}</li>
                          </ul>
                        </div>
                        <Settings
                          ctx={ctx}
                          canEdit
                          section="settings"
                          saveLabel="Save and continue"
                          onSaved={() => markOptional('assistant_settings', 'customized')}
                        />
                        <div className="row" style={{ marginTop: 10 }}>
                          <button type="button" disabled={a.busy} onClick={() => markOptional('assistant_settings', 'defaults_retained')}>
                            Keep defaults and continue
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {complete && (
        <div className="banner ok" role="status" style={{ marginTop: 12 }}>
          <strong>Onboarding complete</strong>
          <p>All guided setup steps are complete and the assistant is active.</p>
          {!repair.smsReady ? (
            <p>Verify the student phone first. Once SMS is ready, {s.preferred_name} can receive assistant notifications and text the assistant number for help.</p>
          ) : data.smsSimulated ? (
            <p>Test messages are simulated in this environment. In production, students receive notifications from the configured assistant number and can text or call it for help.</p>
          ) : data.smsSenderAddress ? (
            <p>
              Going forward, {s.preferred_name} will receive assistant notifications from{' '}
              <span className="mono">{data.smsSenderAddress}</span>. They can also text or call this same number to interact with the assistant.
              Try texting <span className="mono">plan week</span>.
            </p>
          ) : (
            <p>Going forward, {s.preferred_name} will receive assistant notifications from the configured assistant number and can text or call that same number for help.</p>
          )}
          <details className="optional-profile-details">
            <summary>Review completed setup steps</summary>
            <ul>
              <li>Class calendar: {optionalStatusLabel(optionalState.class_calendar)} <button type="button" className="link-button" onClick={() => onGoTo('class-schedule')}>Review class schedule</button></li>
              <li>Sports schedules and packing: {optionalStatusLabel(optionalState.sports)} <button type="button" className="link-button" onClick={() => onGoTo('sports')}>Review sports</button></li>
              <li>Assistant settings: {optionalStatusLabel(optionalState.assistant_settings)} <button type="button" className="link-button" onClick={() => onGoTo('settings')}>Review settings</button></li>
            </ul>
          </details>
          <div className="recommended-next-setup" aria-label="Integration review">
            <div>
              <strong>Integration review</strong>
              <p className="field-help">Configured means setup is saved; usable data depends on the latest sync and imported items.</p>
            </div>
            <dl className="summary-grid">
              <div>
                <dt>Class calendar</dt>
                <dd>
                  {classConnection
                    ? <>
                      {classStatusText}
                      {classConnection.last_sync_at && <div className="field-help">Last synced {fmt.ago(classConnection.last_sync_at)}.</div>}
                      <button type="button" className="link-button" onClick={() => onGoTo('class-schedule')}>Review class schedule</button>
                    </>
                    : <>Not connected. <button type="button" className="link-button" onClick={() => onGoTo('class-schedule')}>Set up class schedule</button></>}
                </dd>
              </div>
              <div>
                <dt>Sports</dt>
                <dd>
                  {sportsTeamCount > 0
                    ? <>
                      {sportsTeamCount} active team{sportsTeamCount === 1 ? '' : 's'} · {sportsGameCount} upcoming game{sportsGameCount === 1 ? '' : 's'} · {notificationsEnabledCount} with reminders on
                      <div><button type="button" className="link-button" onClick={() => onGoTo('sports')}>Review sports schedules</button></div>
                    </>
                    : <>No teams configured. <button type="button" className="link-button" onClick={() => onGoTo('sports')}>Set up sports</button></>}
                </dd>
              </div>
            </dl>
          </div>
          {repair.smsReady && data.smsSenderAddress && (
            <div className="recommended-next-setup" aria-label="Student handoff">
              <div>
                <strong>Student handoff</strong>
                <p className="field-help">Make the first student action the priority after setup.</p>
              </div>
              <p>
                Save <span className="mono">{data.smsSenderAddress}</span> as {s.preferred_name}&apos;s Student Assistant contact.
                On the student&apos;s verified phone, send:
              </p>
              <p className="mono">plan week</p>
              <div className="row">
                {smsHref && <a className="button-link primary" href={smsHref}>Text plan week from student phone</a>}
                <button type="button" onClick={() => onGoTo('today')}>Open dashboard</button>
              </div>
            </div>
          )}
          {recommendedNextSetup.length > 0 && (
            <div className="recommended-next-setup" aria-label="Optional setup you can add later">
              <div>
                <strong>Optional setup you can add later</strong>
                <p className="field-help">These are not required for onboarding, but they unlock more questions parents and students often ask.</p>
              </div>
              <div className="recommended-next-grid">
                {recommendedNextSetup.map((item) => (
                  <button type="button" key={item.key} onClick={() => onGoTo(item.tab)}>
                    <strong>{item.title}</strong>
                    <span>{item.detail}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!complete && (
        <p className="field-help">Class schedule, sports, and email forwarding are optional. They do not block activation and can be changed later.</p>
      )}

      <dl className="summary-grid" style={{ marginTop: 16 }}>
        <div><dt>Child</dt><dd>{s.preferred_name} · Grade {s.grade}</dd></div>
        <div>
          <dt>Assistant SMS number</dt>
          <dd>
            {data.smsSimulated
              ? 'Test messages are simulated here'
              : data.smsSenderAddress
                ? <><span className="mono">{data.smsSenderAddress}</span><div className="field-help">Text this number to reach the assistant.</div></>
                : 'Your assistant number is managed by the configured messaging service.'}
          </dd>
        </div>
      </dl>
      <Notices a={a} />
    </section>
  );
}

/* -------------------------- Student Assistant -------------------------- */

function WorkerHeader({ ctx, me, canEdit, onGoTo }: { ctx: Ctx; me: Me; canEdit: boolean; onGoTo: (tab: string, anchor?: string) => void }) {
  const { data, studentId, reload } = ctx;
  const a = useAction(reload);
  const w = data.worker;
  const s = data.student;
  const state = setupState(data, me);
  const setupNotDone = data.readiness?.ready !== true;
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const [deleteText, setDeleteText] = React.useState('');
  const [confirmingStatus, setConfirmingStatus] = React.useState<'paused' | 'archived' | null>(null);
  const [actionsExpanded, setActionsExpanded] = React.useState(true);
  const repairTarget = !state.permissionsReady
    ? { tab: 'permissions', anchor: undefined }
    : !state.assignmentSourceReady
      ? { tab: 'school', anchor: undefined }
      : !state.phoneVerified
        ? { tab: 'phone', anchor: 'phone-verification-section' }
        : { tab: 'assistant', anchor: undefined };

  const setStatus = (status: string) =>
    a.run(() => api('POST', `/api/students/${studentId}/worker/status`, { status }));

  return (
    <section className="card">
      <div className="spread">
        <div>
          <h2 style={{ marginBottom: 2 }}>{s.preferred_name}&apos;s Student Assistant</h2>
          <div className="muted">
            Grade {s.grade} · {s.timezone} · live status as of {fmt.time(data.liveStatusAt, s.timezone)}
          </div>
        </div>
        <div className="row">
          <Pill tone={statusTone(w?.status)}>{fmt.label(w?.status)}</Pill>
          <Pill tone={state.assignmentSourceReady ? 'ok' : 'warn'}>
            {state.assignmentSourceReady ? 'Canvas ready' : 'Canvas needed'}
          </Pill>
        </div>
      </div>

      {w?.status_reason && (
        <p className="muted">
          {!setupNotDone && w.status !== 'active'
            ? 'Required checks are ready. Choose Resume or Activate in assistant management when you are ready.'
            : w.status_reason}
        </p>
      )}

      {setupNotDone && canEdit && (
        <div className="banner">
          <strong>{initialOnboarding(w) ? 'Initial setup is not complete.' : 'This existing assistant needs attention.'}</strong>
          <ul>{(data.readiness?.blockers ?? []).map((blocker: string) => <li key={blocker}>{blocker}</li>)}</ul>
          <div style={{ marginTop: 8 }}>
            <button
              onClick={() => {
                if (initialOnboarding(w)) onGoTo('setup');
                else onGoTo(repairTarget.tab, repairTarget.anchor);
              }}
            >
              {initialOnboarding(w) ? 'Continue guided setup' : 'Open repair and configuration'}
            </button>
          </div>
        </div>
      )}

      {canEdit && (
        <>
          <div className="row" style={{ marginTop: 10 }}>
            <button onClick={() => setActionsExpanded((value) => !value)} aria-expanded={actionsExpanded}>
              {actionsExpanded ? 'Hide assistant management' : 'Manage assistant'}
            </button>
          </div>
          {actionsExpanded && (
            <div className="row" style={{ marginTop: 10 }}>
              {w?.status !== 'active' && (
                <button className="primary" disabled={a.busy || setupNotDone} onClick={() => setStatus('active')}>
                  {w?.status === 'paused' ? 'Resume assistant' : w?.status === 'archived' ? 'Restore assistant' : 'Activate assistant'}
                </button>
              )}
              {w?.status === 'active' && (
                <button disabled={a.busy} onClick={() => setConfirmingStatus('paused')}>Pause assistant</button>
              )}
              <button disabled={a.busy} onClick={() => setConfirmingStatus('archived')}>Archive</button>
              <button className="danger" disabled={a.busy} onClick={() => setConfirmingDelete(true)}>Unregister child</button>
            </div>
          )}
        </>
      )}
      {confirmingStatus && (
        <div className="confirmation-panel">
          <strong>{confirmingStatus === 'paused' ? 'Pause' : 'Archive'} {s.preferred_name}&apos;s Student Assistant?</strong>
          <p>
            {confirmingStatus === 'paused'
              ? 'Pending reminders will be canceled. You can resume the assistant later.'
              : 'Scheduled notifications will stop and the assistant will be marked archived. You can restore it from management after required checks are ready.'}
          </p>
          <div className="row">
            <button className={confirmingStatus === 'archived' ? 'danger' : 'primary'} disabled={a.busy}
              onClick={async () => {
                await a.run(() => api('POST', `/api/students/${studentId}/worker/status`, { status: confirmingStatus }));
                setConfirmingStatus(null);
              }}>
              Confirm {confirmingStatus === 'paused' ? 'pause' : 'archive'}
            </button>
            <button disabled={a.busy} onClick={() => setConfirmingStatus(null)}>Cancel</button>
          </div>
        </div>
      )}
      {confirmingDelete && (
        <div className="confirmation-panel">
          <strong>Unregister {s.preferred_name}?</strong>
          <div>
            This decommissions {w?.name}, stops scheduled notifications and background tasks, revokes connected school access,
            and starts the 30-day data-retention period. Other children’s assistants are not affected.
          </div>
          <label>
            Enter <strong>{s.preferred_name}</strong> to confirm
            <input value={deleteText} onChange={(event) => setDeleteText(event.target.value)} />
          </label>
          <div className="row">
            <button className="danger" disabled={a.busy || deleteText.toLowerCase() !== s.preferred_name.toLowerCase()}
              onClick={async () => {
                const result = await a.run(
                  () => api('DELETE', `/api/students/${studentId}`, { confirmation: deleteText }),
                );
                if (result?.ok) navigate('/');
              }}>
              {a.busy ? 'Unregister child and remove assistant' : 'Unregister child and remove assistant'}
            </button>
            <button disabled={a.busy} onClick={() => { setConfirmingDelete(false); setDeleteText(''); }}>Cancel</button>
          </div>
        </div>
      )}
      <Notices a={a} />
    </section>
  );
}

function Responsibilities({ ctx, canEdit }: { ctx: Ctx; canEdit: boolean }) {
  const { data, studentId, reload } = ctx;
  const a = useAction(reload);
  const [expanded, setExpanded] = React.useState(true);
  const labels: Record<string, string> = {
    after_school_planning: 'After-school planning',
    prepare_tomorrow: 'Preparation for tomorrow',
    test_preparation: 'Test preparation',
  };
  return (
    <section className="card">
      <div className="spread">
        <h2 className="section-title">
          <span className="illustration-wrap" aria-hidden="true"><NotebookIllustration title="Notebook" /></span>
          Responsibilities
        </h2>
        <button onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
          {expanded ? 'Hide responsibilities' : 'Show responsibilities'}
        </button>
      </div>
      {!expanded ? <p className="muted">The Student Assistant manages these through text.</p> : (<>
      <ul className="plain">
        {data.responsibilities?.map((r: any) => (
          <li key={r.kind} className="row" style={{ justifyContent: 'space-between' }}>
            <span>{labels[r.kind] ?? fmt.label(r.kind)}</span>
            <span className="row">
              <Pill tone={r.enabled ? 'ok' : undefined}>{r.enabled ? 'on' : 'off'}</Pill>
              {canEdit && (
                <button
                  disabled={a.busy}
                  onClick={() => a.run(() => api('POST', `/api/students/${studentId}/worker/responsibilities`,
                    { kind: r.kind, enabled: !r.enabled }))}
                >
                  {r.enabled ? 'Disable' : 'Enable'}
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
      <Notices a={a} />
      </>)}
    </section>
  );
}

/* ------------------------------- tasks ------------------------------- */

function startOfCurrentWeek(date: Date): Date {
  const copy = new Date(date);
  const day = copy.getDay();
  const mondayOffset = (day + 6) % 7;
  copy.setHours(0, 0, 0, 0);
  copy.setDate(copy.getDate() - mondayOffset);
  return copy;
}

function taskPassesActiveWindow(task: any, activeCutoff: number): boolean {
  if (task.origin !== 'school') return true;
  if (task.school_confirmed_submitted_at || task.school_submission_state === 'submitted') return false;
  return !task.due_at || new Date(task.due_at).getTime() >= activeCutoff;
}

function isDueInCurrentWeek(task: any, weekStartMs: number, nextWeekStartMs: number): boolean {
  if (!task.due_at) return false;
  const dueMs = new Date(task.due_at).getTime();
  if (!Number.isFinite(dueMs)) return false;
  return dueMs >= weekStartMs && dueMs < nextWeekStartMs;
}

function Tasks({ ctx }: { ctx: Ctx }) {
  const { data, studentId, reload } = ctx;
  const a = useAction(reload);
  const [title, setTitle] = React.useState('');
  const [kind, setKind] = React.useState('assignment');
  const [due, setDue] = React.useState('');
  const [showDone, setShowDone] = React.useState(false);
  const [expanded, setExpanded] = React.useState(true);
  const [showAllOpen, setShowAllOpen] = React.useState(false);

  const activeCutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const weekStart = startOfCurrentWeek(new Date());
  const nextWeekStart = new Date(weekStart);
  nextWeekStart.setDate(nextWeekStart.getDate() + 7);
  const weekStartMs = weekStart.getTime();
  const nextWeekStartMs = nextWeekStart.getTime();
  const baseTasks = (data.tasks ?? []).filter((t: any) => {
    if (!showDone && t.status !== 'open') return false;
    return taskPassesActiveWindow(t, activeCutoff);
  });
  const tasks = showAllOpen
    ? baseTasks
    : baseTasks.filter((t: any) => isDueInCurrentWeek(t, weekStartMs, nextWeekStartMs));

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    await a.run(() => api('POST', `/api/students/${studentId}/tasks`, {
      title, kind, dueAt: due ? new Date(due).toISOString() : null,
    }), 'Added.');
    setTitle(''); setDue('');
  };

  return (
    <section className="card">
      <div className="spread">
        <h2 className="section-title">
          <span className="illustration-wrap" aria-hidden="true"><NotebookIllustration title="Notebook" /></span>
          Work and tests
        </h2>
        <button onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
          {expanded ? 'Hide work' : `Show current work (${tasks.length})`}
        </button>
      </div>
      {!expanded ? (
        <p className="muted">The Student Assistant handles reminders and text replies automatically.</p>
      ) : (<>
        <label className="row" style={{ margin: '10px 0' }}>
          <input type="checkbox" style={{ width: 18, minHeight: 18 }} checked={showDone}
            onChange={(e) => setShowDone(e.target.checked)} /> show completed
        </label>
        <label className="row" style={{ margin: '10px 0' }}>
          <input
            type="checkbox"
            style={{ width: 18, minHeight: 18 }}
            checked={showAllOpen}
            onChange={(e) => setShowAllOpen(e.target.checked)}
          /> show all active tasks
        </label>
        {!showAllOpen && (
          <p className="muted" style={{ marginTop: 0 }}>
            Showing tasks due this week.
          </p>
        )}

        <form onSubmit={add} className="grid" style={{ alignItems: 'end' }}>
        <Field label="Add a task or test" value={title} onChange={setTitle} required placeholder="Science test" />
        <Field label="Kind" value={kind} onChange={setKind} options={[
          { value: 'assignment', label: 'Assignment' }, { value: 'test', label: 'Test' },
          { value: 'chore', label: 'Chore' }, { value: 'other', label: 'Other' },
        ]} />
        <Field label="Due" type="datetime-local" value={due} onChange={setDue} />
        <button className="primary" type="submit" disabled={a.busy || !title}>Add</button>
        </form>
        <Notices a={a} />

        {tasks.length ? (
        <table>
          <thead>
            <tr><th>Task</th><th>Due</th><th>Source</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr>
          </thead>
          <tbody>
            {tasks.map((t: any) => (
              <tr key={t.id}>
                <td>
                  {t.source_url
                    ? <a href={t.source_url} target="_blank" rel="noreferrer noopener">{t.title}</a>
                    : t.title}
                  {t.kind === 'test' && <> <Pill>test</Pill></>}
                </td>
                <td>{t.due_at ? fmt.dateTime(t.due_at, data.student.timezone) : <span className="muted">no due date given</span>}</td>
                <td className="muted">
                  {t.origin === 'school' ? `${fmt.label(t.provider)} sync` : 'entered manually'}
                  {t.school_removed_at && <> · <Pill tone="warn">removed at school</Pill></>}
                </td>
                <td>
                  {t.status === 'done' ? <Pill tone="ok">done</Pill> : <Pill>open</Pill>}
                  <div className="muted">
                    {t.school_confirmed_submitted_at
                      ? `school confirmed ${fmt.ago(t.school_confirmed_submitted_at)}`
                      : t.student_reported_done_at
                        ? `student said done ${fmt.ago(t.student_reported_done_at)} · not confirmed by school`
                        : ''}
                  </div>
                </td>
                <td>
                  <div className="row">
                    {t.status === 'open'
                      ? <button disabled={a.busy} onClick={() => a.run(() => api('POST', `/api/students/${studentId}/tasks/${t.id}/done`))}>Mark done</button>
                      : <button disabled={a.busy} onClick={() => a.run(() => api('POST', `/api/students/${studentId}/tasks/${t.id}/reopen`))}>Reopen</button>}
                    {t.origin !== 'school' && (
                      <button className="danger" disabled={a.busy}
                        onClick={() => a.run(() => api('DELETE', `/api/students/${studentId}/tasks/${t.id}`))}>Delete</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        ) : <Empty>No current work recorded. Check Canvas for anything outside the active work list.</Empty>}
      </>)}
    </section>
  );
}

/* ---------------------- activities ---------------------- */

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function Activities({ ctx, canEdit }: { ctx: Ctx; canEdit: boolean }) {
  const { data, studentId, reload } = ctx;
  const a = useAction(reload);
  const [f, setF] = React.useState({ name: '', kind: 'sport', startTime: '15:30', days: [] as number[] });
  const [showAddActivity, setShowAddActivity] = React.useState(false);
  const [intakeText, setIntakeText] = React.useState('');
  const [intakeProposal, setIntakeProposal] = React.useState<any>(null);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editText, setEditText] = React.useState('');
  const proposals = (data.intakeProposals ?? []) as any[];
  const emails = (data.emailIntake ?? []) as any[];

  const addActivity = async (e: React.FormEvent) => {
    e.preventDefault();
    await a.run(() => api('POST', `/api/students/${studentId}/activities`, {
      name: f.name, kind: f.kind, startTime: f.startTime,
      daysOfWeek: f.days, packingListId: null, remindBeforeDeparture: true,
    }), 'Activity added and reminders recalculated.');
    setF({ ...f, name: '', days: [] });
  };

  const cancelOccurrence = (activityId: string, occursOn: string) =>
    a.run(() => api('POST', `/api/students/${studentId}/occurrences`, {
      activityId, occursOn, status: 'canceled',
    }), 'Canceled.');

  const previewIntake = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await a.run(() => api('POST', `/api/students/${studentId}/intake/proposals`, {
      text: intakeText, source: 'website',
    }));
    if (result?.ok) setIntakeProposal(result);
  };

  const confirmIntake = async () => {
    if (!intakeProposal?.proposal?.id) return;
    await a.run(() => api('POST', `/api/students/${studentId}/intake/proposals/${intakeProposal.proposal.id}/confirm`),
      intakeProposal.proposal.requiresParentApproval
        ? 'Sent for parent approval.'
        : 'Activity added and reminders recalculated.');
    setIntakeProposal(null);
    setIntakeText('');
  };

  const updateProposal = async (proposalId: string, successNote: string) => {
    await a.run(
      () => api('PATCH', `/api/students/${studentId}/intake/proposals/${proposalId}`, { text: editText }),
      successNote,
    );
    setEditingId(null);
    setEditText('');
  };

  return (
    <section className="card">
      <div className="spread">
        <h2 className="section-title">
          <span className="illustration-wrap" aria-hidden="true"><BackpackIllustration title="Backpack" /></span>
          Activities
        </h2>
        <button className="primary" type="button" onClick={() => setShowAddActivity((value) => !value)} aria-expanded={showAddActivity}>
          {showAddActivity ? 'Close' : 'Add activity'}
        </button>
      </div>
      <form onSubmit={previewIntake} className="grid" style={{ alignItems: 'end', marginBottom: 16 }}>
        <Field label="Tell me about an activity" value={intakeText} onChange={setIntakeText}
          placeholder="Add basketball prac tues at 4, bring water" />
        <button type="submit" disabled={a.busy || !intakeText.trim()}>Preview</button>
      </form>
      {intakeProposal && (
        <div className="notice" role="status">
          <strong>Check this first</strong>
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{intakeProposal.preview}</pre>
          {intakeProposal.proposal?.missing?.length ? (
            <p className="muted">Still needed: {intakeProposal.proposal.missing.join(' and ')}.</p>
          ) : (
            <div className="row">
              <button className="primary" type="button" disabled={a.busy || intakeProposal.proposal?.requiresParentApproval} onClick={confirmIntake}>
                {intakeProposal.proposal?.requiresParentApproval ? 'Needs parent approval' : 'Add it'}
              </button>
              {!intakeProposal.proposal?.requiresParentApproval && <button type="button" onClick={() => setIntakeProposal(null)}>Not quite</button>}
            </div>
          )}
        </div>
      )}
      {(proposals.length > 0 || emails.length > 0) && (
        <>
          <h3>Activity requests from email</h3>
          {proposals.length > 0 ? (
            <div className="notice" role="status" style={{ marginBottom: 16 }}>
              <strong>Email could not be added as an activity yet</strong>
              <p className="field-help" style={{ marginTop: 6 }}>
                Parent action needed: review what the assistant understood and either complete the missing details or add the safe activity manually.
              </p>
              {emails.length > 0 && (
                <p className="field-help">
                  Latest forwarded email arrived {fmt.dateTime(emails[0].receivedAt, data.student.timezone)}.
                </p>
              )}
              {proposals.map((p: any) => (
                <div key={p.id} style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
                  <div className="muted">
                    Received {fmt.dateTime(p.createdAt, data.student.timezone)} · {fmt.label(p.status)}
                  </div>
                  <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: '6px 0 0' }}>{p.preview}</pre>
                  {p.proposal?.requiresParentApproval ? (
                    <>
                      <p className="muted">
                        This email includes sensitive pickup, ride-share, or sharing instructions. The assistant will not add it as an activity from email. Parent action needed: add any safe activity details manually.
                      </p>
                      {canEdit ? (
                        <button
                          type="button"
                          disabled={a.busy}
                          onClick={() => a.run(
                            () => api('POST', `/api/students/${studentId}/intake/proposals/${p.id}/reject`),
                            'Sensitive email request dismissed.',
                          )}
                        >
                          Dismiss
                        </button>
                      ) : <span className="muted">Ask your parent to add this activity manually.</span>}
                    </>
                  ) : p.status === 'expired' ? (
                    <>
                      <p className="muted">This email request expired before it became an activity. Add or confirm details to bring it back.</p>
                      {editingId === p.id ? (
                        <form
                          className="grid"
                          style={{ alignItems: 'end', marginTop: 8 }}
                          onSubmit={async (event) => {
                            event.preventDefault();
                            await updateProposal(p.id, 'Request restored. Review it before adding it.');
                          }}
                        >
                          <Field label="Confirm or add details" value={editText} onChange={setEditText}
                            placeholder="Basketball practice every Tuesday at 4 PM, bring water" />
                          <button className="primary" type="submit" disabled={a.busy || !editText.trim()}>Restore request</button>
                        </form>
                      ) : (
                        <button type="button" disabled={a.busy} onClick={() => { setEditingId(p.id); setEditText(''); }}>
                          Restore request
                        </button>
                      )}
                    </>
                  ) : p.proposal?.missing?.length ? (
                    <>
                      <p className="muted">The assistant did not add this from email because it still needs: {p.proposal.missing.join(' and ')}.</p>
                      {editingId === p.id ? (
                        <form
                          className="grid"
                          style={{ alignItems: 'end', marginTop: 8 }}
                          onSubmit={async (event) => {
                            event.preventDefault();
                            await updateProposal(p.id, 'Details updated. Review it before adding it.');
                          }}
                        >
                          <Field label="Add the missing details" value={editText} onChange={setEditText}
                            placeholder="Basketball practice every Tuesday at 4 PM, bring water" />
                          <button className="primary" type="submit" disabled={a.busy || !editText.trim()}>Update request</button>
                        </form>
                      ) : (
                        <button type="button" disabled={a.busy} onClick={() => { setEditingId(p.id); setEditText(''); }}>
                          Complete details
                        </button>
                      )}
                    </>
                  ) : canEdit ? (
                    <div className="row" style={{ marginTop: 8 }}>
                      <button
                        className="primary"
                        type="button"
                        disabled={a.busy}
                        onClick={() => a.run(
                          () => api('POST', `/api/students/${studentId}/intake/proposals/${p.id}/approve`),
                          'Approved and added to Activities.',
                        )}
                      >
                        Approve and add activity
                      </button>
                      <button
                        type="button"
                        disabled={a.busy}
                        onClick={() => a.run(
                          () => api('POST', `/api/students/${studentId}/intake/proposals/${p.id}/reject`),
                          'Request dismissed.',
                        )}
                      >
                        Dismiss
                      </button>
                    </div>
                  ) : <span className="muted">Needs parent approval.</span>}
                </div>
              ))}
            </div>
          ) : (
            <div className="notice" role="status" style={{ marginBottom: 16 }}>
              <strong>Forwarded email received</strong>
              <p className="muted" style={{ marginBottom: 0 }}>
                Email arrived {fmt.dateTime(emails[0].receivedAt, data.student.timezone)}, but the assistant could not turn it into an activity. Parent action needed: add the activity manually from the email details.
              </p>
            </div>
          )}
        </>
      )}
      {showAddActivity && (
        <form onSubmit={async (e) => { await addActivity(e); setShowAddActivity(false); }} className="grid" style={{ alignItems: 'end', marginBottom: 16 }}>
          <Field label="Activity name" value={f.name} onChange={(v) => setF({ ...f, name: v })} placeholder="Basketball practice" />
          <Field label="Kind" value={f.kind} onChange={(v) => setF({ ...f, kind: v })} options={[
            { value: 'sport', label: 'Sport' }, { value: 'pe', label: 'PE' }, { value: 'music', label: 'Music' },
            { value: 'club', label: 'Club' }, { value: 'field_trip', label: 'Field trip' }, { value: 'other', label: 'Other' },
          ]} />
          <Field label="Starts at" type="time" value={f.startTime} onChange={(v) => setF({ ...f, startTime: v })} />
          <div>
            <label>Repeats on</label>
            <div className="row">
              {DAYS.map((d, i) => (
                <label key={d} className="row" style={{ margin: 0, gap: 4 }}>
                  <input
                    type="checkbox"
                    style={{ width: 18, minHeight: 18 }}
                    checked={f.days.includes(i + 1)}
                    onChange={(e) => setF({
                      ...f,
                      days: e.target.checked ? [...f.days, i + 1] : f.days.filter((x) => x !== i + 1),
                    })}
                  />{d}
                </label>
              ))}
            </div>
          </div>
          <button className="primary" type="submit" disabled={a.busy || !f.name}>Save activity</button>
        </form>
      )}

      <h3>Activities</h3>
      {data.activities?.length ? (
        <ul className="plain">
          {data.activities.map((act: any) => (
            <li key={act.id}>
              <div className="spread">
                <div className="activity-details">
                  <strong>{act.name}</strong> <span className="muted">{fmt.label(act.kind)}</span>
                  <div className="muted">
                    {(act.days_of_week ?? []).map((d: number) => DAYS[d - 1]).join(', ') || 'no recurring days'}
                    {act.start_time ? ` · ${fmt.clockTime(String(act.start_time).slice(0, 5))}` : ''}
                  </div>
                </div>
                <button className="danger" disabled={a.busy}
                  onClick={() => a.run(() => api('DELETE', `/api/students/${studentId}/activities/${act.id}`))}>Remove</button>
              </div>
            </li>
          ))}
        </ul>
      ) : <Empty>No activities yet.</Empty>}

      <Notices a={a} />

      <h3>Tomorrow ({data.tomorrow?.date})</h3>
      {data.tomorrow?.occurrences?.length ? (
        <ul className="plain">
          {data.tomorrow.occurrences.map((o: any, i: number) => (
            <li key={i} className="spread">
              <div>
                <strong>{o.name}</strong>{' '}
                {o.status === 'canceled' ? <Pill tone="bad">canceled</Pill> : <Pill tone="ok">scheduled</Pill>}
                <div className="muted">{o.startTime ? String(o.startTime).slice(0, 5) : 'time not set'}</div>
              </div>
              {o.status !== 'canceled' && o.activityId && (
                <button disabled={a.busy} onClick={() => cancelOccurrence(o.activityId, data.tomorrow.date)}>
                  Cancel just this day
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : <Empty>Nothing scheduled for tomorrow.</Empty>}
    </section>
  );
}

/* ------------------------------ reminders ------------------------------ */

function Reminders({ ctx }: { ctx: Ctx }) {
  const { data, studentId, reload } = ctx;
  const a = useAction(reload);
  const tz = data.student.timezone;
  const [preview, setPreview] = React.useState<any>(null);
  const [expanded, setExpanded] = React.useState(false);

  const loadPreview = async () => {
    const res = await api('GET', `/api/students/${studentId}/reminders/preview`);
    setPreview(res);
  };
  React.useEffect(() => { loadPreview().catch(() => {}); /* eslint-disable-next-line */ }, [studentId]);

  const pending = (data.reminders ?? []).filter((r: any) => r.status === 'pending');
  const past = (data.reminders ?? []).filter((r: any) => r.status !== 'pending').slice(0, 12);
  const nextReminder = pending[0];

  return (
    <section className="card">
      <div className="spread">
        <div>
          <h2 className="section-title">
            <span className="illustration-wrap" aria-hidden="true"><CalendarStarIllustration title="Calendar" /></span>
            Reminders
          </h2>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            The assistant schedules these automatically from schoolwork, activities, and your child&apos;s preferences.
            Review them here or change a one-time reminder when needed.
          </p>
        </div>
        <button onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
          {expanded ? 'Hide reminder details' : 'Show reminder details'}
        </button>
      </div>
      {!expanded ? (
        <p className="field-help" style={{ marginBottom: 0 }}>
          {pending.length
            ? `${pending.length} upcoming reminder${pending.length === 1 ? '' : 's'}${nextReminder?.scheduled_for ? ` · next: ${fmt.dateTime(nextReminder.scheduled_for, tz)}` : ''}.`
            : 'No upcoming reminders are scheduled right now.'}
          {' '}No action is needed unless you want to review or change one.
        </p>
      ) : (<>
      <div className="banner" style={{ marginTop: 12 }}>
        <strong>Refresh after a change</strong>
        <p className="field-help" style={{ margin: '4px 0 8px' }}>
          Use this after changing a school connection, activity, or reminder preference. Normal scheduling happens automatically.
        </p>
        <button disabled={a.busy}
          onClick={() => a.run(() => api('POST', `/api/students/${studentId}/reminders/reconcile`), 'Upcoming reminders refreshed.')}>
          Refresh upcoming reminders
        </button>
      </div>

      <h3>Scheduled to send</h3>
      {pending.length ? (
        <ul className="plain">
          {pending.map((r: any) => (
            <li key={r.id}>
              <div className="spread">
                <div>
                  <strong>{fmt.label(r.kind)}</strong>
                  <div className="muted">{r.reason?.why}</div>
                  <div className="mono" style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{r.body}</div>
                  {(r.reason?.sources ?? []).length > 0 && (
                    <div className="muted" style={{ marginTop: 4 }}>
                      Sources: {r.reason.sources.map((s: any, i: number) => (
                        <span key={i}>
                          {i > 0 && ' · '}
                          {s.url ? <a href={s.url} target="_blank" rel="noreferrer noopener">{s.label}</a> : s.label}
                          {' '}<span className="muted">({s.freshness})</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div>{fmt.dateTime(r.scheduled_for, tz)}</div>
                  <div className="row" style={{ justifyContent: 'flex-end', marginTop: 6 }}>
                    <button disabled={a.busy}
                      onClick={() => a.run(() => api('POST', `/api/students/${studentId}/reminders/${r.id}/cancel`, { reason: 'Canceled from Command Center' }))}>
                      Cancel
                    </button>
                    <button disabled={a.busy} onClick={() => {
                      const when = prompt('Reschedule to (YYYY-MM-DDTHH:MM local):',
                        new Date(r.scheduled_for).toISOString().slice(0, 16));
                      if (when) a.run(() => api('POST', `/api/students/${studentId}/reminders/${r.id}/reschedule`,
                        { scheduledFor: new Date(when).toISOString() }));
                    }}>Reschedule</button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : <Empty>No scheduled reminders right now.</Empty>}
      <Notices a={a} />

      <h3>Preview for {preview?.date ?? 'tomorrow'}</h3>
      {preview
        ? preview.reminders.length
          ? (
            <ul className="plain">
              {preview.reminders.map((r: any, i: number) => (
                <li key={i}>
                  <div className="spread">
                    <strong>{fmt.label(r.kind)}</strong>
                    <span className="muted">{fmt.dateTime(r.scheduledFor, tz)}</span>
                  </div>
                  <div className="muted">{r.reason?.why}</div>
                  <div className="mono" style={{ whiteSpace: 'pre-wrap' }}>{r.body}</div>
                </li>
              ))}
            </ul>
          )
          : <Empty>Nothing would be sent tomorrow.</Empty>
        : <Empty>Loading preview…</Empty>}

      <h3>Recent</h3>
      {past.length ? (
        <table>
          <thead><tr><th>Kind</th><th>Scheduled</th><th>Status</th><th>Delivery</th></tr></thead>
          <tbody>
            {past.map((r: any) => (
              <tr key={r.id}>
                <td>{fmt.label(r.kind)}</td>
                <td>{fmt.dateTime(r.scheduled_for, tz)}</td>
                <td><Pill tone={statusTone(r.status)}>{fmt.label(r.status)}</Pill><div className="muted">{r.status_reason}</div></td>
                <td className="muted">
                  {r.sent_at ? `accepted by provider ${fmt.ago(r.sent_at)}` : '—'}
                  {r.delivery_status ? ` · carrier: ${fmt.label(r.delivery_status)}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <Empty>No reminder history yet.</Empty>}
      </>)}
    </section>
  );
}

/* ------------------------------- school ------------------------------- */

function School({ ctx, guided = false }: { ctx: Ctx; guided?: boolean }) {
  const { data, studentId, reload } = ctx;
  const a = useAction(reload);
  const readiness = useAsync(() => api('GET', '/api/school/readiness'), []);
  const [canvasUrl, setCanvasUrl] = React.useState(
    () => inferCanvasBaseUrlFromSchoolName(data.student.school_name ?? '') ?? 'https://bush.instructure.com',
  );
  const [canvasFeedUrl, setCanvasFeedUrl] = React.useState('');
  const [canvasCode, setCanvasCode] = React.useState('');
  const [canvasConnectMode, setCanvasConnectMode] = React.useState<'calendar' | 'token'>('calendar');
  const canvasCodeRef = React.useRef<HTMLInputElement | null>(null);
  const [canvasConnected, setCanvasConnected] = React.useState(false);
  const [expanded, setExpanded] = React.useState(true);
  const canvasConnection = data.connections?.find((c: any) => c.provider === 'canvas');
  const reconnectingCanvas = canvasConnection?.status === 'needs_reconnection';
  const reconnectHint = canvasConnection?.mode === 'calendar_feed'
    ? 'Reconnect using the same Canvas calendar link. Existing reminders and settings stay in place.'
    : 'Reconnect with a fresh Canvas access token. Existing reminders and settings stay in place.';
  // School name is collected at child-profile creation but must stay editable
  // here so parents can correct it (e.g. after connecting Canvas) without
  // recreating the student profile.
  const [schoolName, setSchoolName] = React.useState(data.student.school_name ?? '');
  React.useEffect(() => {
    setSchoolName(data.student.school_name ?? '');
  }, [data.student.school_name]);
  React.useEffect(() => {
    const inferred = inferCanvasBaseUrlFromSchoolName(schoolName);
    if (!inferred) return;
    setCanvasUrl((current) => (current.trim() ? current : inferred));
  }, [schoolName]);
  React.useEffect(() => {
    if (!canvasConnection || canvasConnection.status === 'connected') return;
    setCanvasConnectMode(canvasConnection.mode === 'personal_token' ? 'token' : 'calendar');
  }, [canvasConnection?.id, canvasConnection?.status, canvasConnection?.mode]);
  const schoolNameDirty = schoolName.trim() !== (data.student.school_name ?? '').trim();
  useDraftProtection(schoolNameDirty || Boolean(canvasCode) || Boolean(canvasFeedUrl.trim()));
  const postCanvasSetupStage = React.useCallback(
    async (
      stage: 'automation_started' | 'token_created' | 'feed_link_opened' | 'feed_link_saved'
        | 'manual_token_entered' | 'token_delivered_to_app'
        | 'payload_without_token' | 'helper_window_closed' | 'connect_failed',
      source: 'helper' | 'manual' | 'feed',
      baseUrl?: string,
    ) => {
      try {
        await api('POST', `/api/students/${studentId}/school/canvas/setup-telemetry`, {
          stage,
          source,
          baseUrl: baseUrl?.trim() || undefined,
        });
      } catch (error) {
        console.warn('canvas setup telemetry failed', { stage, source, error });
      }
    },
    [studentId],
  );

  const canvasCalendarPageUrl = (() => {
    try {
      const parsed = new URL(canvasUrl.trim());
      if (parsed.protocol !== 'https:') return null;
      return `${parsed.toString().replace(/\/+$/, '')}/calendar`;
    } catch {
      return null;
    }
  })();
  const canvasFeedLooksLikeEmail = looksLikeEmail(canvasFeedUrl);
  const canvasFeedError = canvasFeedLooksLikeEmail
    ? 'This looks like an email address. Paste the calendar feed link from Canvas.'
    : undefined;

  return (
    <section className="card" id="student-school" tabIndex={-1}>
      <div className="spread">
        <h2 className="section-title">
          <span className="illustration-wrap" aria-hidden="true"><PaperAirplaneIllustration title="Connection" /></span>
          Connect school
        </h2>
        {!guided && <button onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
          {expanded ? 'Hide school setup' : 'Show school setup'}
        </button>}
      </div>
      {!expanded ? (
        <p className="muted">Canvas remains the active school system for this student.</p>
      ) : (<>

      <div hidden={guided} className="banner" style={{ marginBottom: 12 }}>
        <strong>School name</strong>
        <p className="field-help">
          Used to match Sports schedules and shown across the assistant. Update it any time — for example, after
          connecting Canvas or if it was entered incorrectly during setup.
        </p>
        <div className="grid">
          <Field label="School name" value={schoolName} onChange={setSchoolName} placeholder="e.g. Lincoln High School" />
        </div>
        <button className="primary" style={{ marginTop: 6 }} disabled={a.busy || !schoolNameDirty}
          onClick={() => a.run(
            () => api('PATCH', `/api/students/${studentId}`, { schoolName: schoolName.trim() }),
            'School name saved.',
          )}>
          {a.busy ? 'Saving…' : 'Save school name'}
        </button>
      </div>

      {data.connections?.length ? (
        <ul className="plain">
          {data.connections.map((c: any) => (
            <li key={c.id}>
              <div className="spread">
                <div>
                  <strong>{fmt.label(c.provider)}</strong>{' '}
                  <Pill tone={statusTone(c.status)}>{fmt.label(c.status)}</Pill>{' '}
                  {c.mode === 'fixture' && <Pill tone="warn">FIXTURE DATA — not a real school</Pill>}
                  <div className="muted">
                    {c.external_user_name ? `${c.external_user_name} · ` : ''}
                    school data last synchronized {fmt.ago(c.last_sync_at)}
                    {c.last_sync_status ? ` (${fmt.label(c.last_sync_status)})` : ''}
                    {c.consecutive_failures > 0 ? ` · ${c.consecutive_failures} consecutive failures` : ''}
                    {` · polling every ${c.sync_interval_min} min`}
                  </div>
                  {c.last_error && <div className="muted">{c.last_error}</div>}
                </div>
                <div className="row">
                  <button disabled={a.busy}
                    onClick={() => a.run(() => api('POST', `/api/students/${studentId}/school/${c.id}/sync`), 'Sync requested.')}>
                    Sync now
                  </button>
                  <button className="danger" disabled={a.busy}
                    onClick={() => a.run(() => api('POST', `/api/students/${studentId}/school/${c.id}/disconnect`), 'Disconnected and tokens revoked where supported.')}>
                    Disconnect
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="banner">
          <strong>Canvas is not connected.</strong> You can still add tasks and activities manually, but activation stays blocked until Canvas is connected and the first assignment sync succeeds. Feed-first calendar setup is recommended.
        </div>
      )}
      {canvasConnection && !data.readiness?.assignmentSource?.ready && (
        <p className="banner" role="status">
          {canvasConnection.status !== 'connected'
            ? canvasConnection.mode === 'calendar_feed'
              ? 'Canvas needs the calendar link reconnected before the assistant can activate or resume.'
              : 'Canvas needs to be reconnected before the assistant can activate or resume.'
            : canvasConnection.last_sync_status === 'failed'
            ? 'The first Canvas assignment sync did not complete. Choose Sync now above to retry; activation remains unavailable until a sync succeeds.'
            : 'Canvas is connected and the first assignment sync is running automatically. Most families see this complete in about 1-2 minutes — no extra click is needed.'}
        </p>
      )}
      {!data.readiness?.assignmentSource?.ready && data.connections?.length > 0 && !canvasConnection && (
        <p className="banner">This connection does not provide a supported assignment sync yet. Canvas is currently required for activation.</p>
      )}

      <h3>Connect Canvas homework</h3>
      {readiness.loading && <Empty>Checking…</Empty>}
      {readiness.data && (!canvasConnection?.status || canvasConnection.status !== 'connected') && (
        <div className="banner">
          <strong>Choose how to connect Canvas</strong>
          <p className="field-help">{reconnectingCanvas ? reconnectHint : 'Choose how you want to connect.'}</p>
          <div className="tabs-segmented" aria-label="Canvas connection options" style={{ marginTop: 10 }}>
            <button
              type="button"
              aria-current={canvasConnectMode === 'calendar' ? 'true' : undefined}
              onClick={() => setCanvasConnectMode('calendar')}
            >
              Use a calendar link
            </button>
            <button
              type="button"
              aria-current={canvasConnectMode === 'token' ? 'true' : undefined}
              onClick={() => setCanvasConnectMode('token')}
            >
              Use an access token
            </button>
          </div>
          {canvasConnectMode === 'calendar' && (
            <div style={{ marginTop: 8 }}>
              <p className="field-help">
                Read-only calendar access. No access token to create.
              </p>
              <p className="field-help">
                Completed assignments may still appear. Mark them done in Student Assistant.
              </p>
              <hr />
              <h4>1. Open your student's Canvas calendar</h4>
              <div className="row" style={{ alignItems: 'end', marginTop: 8 }}>
                <div style={{ flex: '1 1 320px' }}>
                  <Field label="School Canvas website" value={canvasUrl} onChange={setCanvasUrl} type="url" />
                </div>
                <button
                  type="button"
                  disabled={a.busy || !canvasCalendarPageUrl}
                  onClick={() => {
                    if (!canvasCalendarPageUrl) return;
                    void postCanvasSetupStage('feed_link_opened', 'feed', canvasUrl);
                    window.open(canvasCalendarPageUrl, '_blank', 'noopener,noreferrer');
                  }}
                >
                  Open Canvas calendar
                </button>
              </div>
              <p className="field-help">Check that this is your school's Canvas address. Sign in to the account that shows your student's assignments.</p>
              {!canvasCalendarPageUrl && (
                <p className="field-help">Enter a valid HTTPS Canvas website to open the calendar page.</p>
              )}
              <hr />
              <h4>2. Copy the calendar link from Canvas</h4>
              <p className="field-help">In Canvas, select <strong>Calendar Feed</strong>, then copy the full link shown.</p>
              <p className="field-help">Example only — your link will be different:</p>
              <p className="field-help"><code>https://bush.instructure.com/feeds/calendars/your-private-code.ics</code></p>
              <p className="field-help">Copy the feed link, not the calendar page's address.</p>
              <hr />
              <h4>3. Paste your link and connect</h4>
              <Field
                label="Canvas calendar link"
                value={canvasFeedUrl}
                onChange={setCanvasFeedUrl}
                type="url"
                placeholder="Paste the full calendar feed link here"
                error={canvasFeedError}
              />
              <div className="row" style={{ marginTop: 8 }}>
                <button
                  className="primary"
                  type="button"
                  disabled={a.busy || !canvasFeedUrl.trim() || canvasFeedLooksLikeEmail}
                  onClick={() => a.run(async () => {
                    const baseUrl = canvasUrl.trim();
                    const feedUrl = canvasFeedUrl.trim();
                    await api('POST', `/api/students/${studentId}/school/canvas/assignment-feed/validate`, {
                      baseUrl,
                      feedUrl,
                    });
                    await postCanvasSetupStage('feed_link_saved', 'feed', baseUrl);
                    const connected = await api('POST', `/api/students/${studentId}/school/canvas/assignment-feed`, {
                      baseUrl,
                      feedUrl,
                    });
                    setCanvasConnected(Boolean(connected?.ok));
                    setCanvasFeedUrl('');
                    a.setNote(
                      reconnectingCanvas
                        ? 'Canvas calendar link reconnected. Existing settings were kept and assignment sync is running now.'
                        : (connected?.message ?? 'Canvas assignment feed connected. Assignment sync is running now.'),
                    );
                  })}
                >
                  {a.busy ? 'Connecting…' : 'Check link & connect'}
                </button>
              </div>
            </div>
          )}
          {canvasConnectMode === 'token' && (
            <div style={{ marginTop: 8 }}>
              <p className="field-help">
                More technical setup: create and paste a personal access token.
              </p>
              <p className="field-help">
                May include more assignment details, including submission status, depending on your school's Canvas data.
              </p>
              <ol>
                <li>Open Canvas Settings for the student's school account.</li>
                <li>Create a <strong>New Access Token</strong> named <strong>Student Assistant</strong> and choose the maximum expiration date your school allows.</li>
                <li>Paste the token below and connect.</li>
              </ol>
              <p>
                {canvasProfileSettingsUrl(canvasUrl)
                  ? (
                    <a href={canvasProfileSettingsUrl(canvasUrl)!} target="_blank" rel="noreferrer">
                      Open Canvas Settings
                    </a>
                  )
                  : <span className="muted">Enter a valid HTTPS Canvas website to open settings.</span>}
              </p>
              <Field
                label="Canvas access token (required)"
                value={canvasCode}
                onChange={setCanvasCode}
                inputRef={canvasCodeRef}
                type="password"
                placeholder="Paste the token from Canvas"
              />
              <button className="primary" disabled={a.busy || !canvasCode.trim()} onClick={() => {
                void postCanvasSetupStage('manual_token_entered', 'manual', canvasUrl);
                void postCanvasSetupStage('token_delivered_to_app', 'manual', canvasUrl);
                return a.run(
                  () => api('POST', `/api/students/${studentId}/school/canvas/personal-token`, {
                    baseUrl: canvasUrl.trim(), accessToken: canvasCode.trim(),
                  }),
                ).then((res) => {
                  if (res && res.ok !== false) {
                    setCanvasCode('');
                    setCanvasConnected(true);
                    a.setNote(
                      reconnectingCanvas
                        ? 'Canvas connection restored. Existing settings were kept and assignment sync is running now.'
                        : (res?.message ?? 'Canvas connected. The first assignment sync starts automatically and usually finishes within 1-2 minutes.'),
                    );
                  } else {
                    void postCanvasSetupStage('connect_failed', 'manual', canvasUrl);
                  }
                });
              }}>
                {a.busy ? 'Connecting…' : 'Save and connect Canvas token'}
              </button>
            </div>
          )}
          {canvasConnected && (
            <div className="banner ok" role="status" style={{ marginTop: 10 }}>
              <strong>Canvas connected.</strong> The first assignment sync starts automatically and usually finishes within 1-2 minutes.
            </div>
          )}
        </div>
      )}

      {readiness.data && canvasConnection?.status === 'connected' && !guided && (
        <p className="muted">
          <strong>Canvas</strong>{' '}
          {data.readiness?.assignmentSource?.ready
            ? 'is connected and the first successful sync is complete.'
            : 'is connected and the first sync runs automatically. Most families see it complete in about 1-2 minutes; use Sync now only if it does not finish.'}
        </p>
      )}

      {readiness.data && !guided && (
        <p className="muted">
          <strong>Google Classroom:</strong> {readiness.data.googleClassroom.detail}
        </p>
      )}

      {data.devTools && (
        <div style={{ marginTop: 10 }}>
          <button disabled={a.busy}
            onClick={() => a.run(() => api('POST', `/api/students/${studentId}/school/fixture-connect`), 'Fixture connection attached. All records are labelled [FIXTURE].')}>
            Attach labelled fixture data (development only)
          </button>
        </div>
      )}

      <h3>Recent sync runs</h3>
      <p className="muted">A successful run with 0 / 0 / 0 / 0 means the sync request completed but no assignments were returned in that run.</p>
      {data.syncRuns?.length ? (
        <table>
          <thead><tr><th>Started</th><th>Trigger</th><th>Status</th><th>Seen / new / updated / removed</th></tr></thead>
          <tbody>
            {data.syncRuns.map((r: any) => (
              <tr key={r.id}>
                <td>{fmt.dateTime(r.started_at, data.student.timezone)}</td>
                <td>{fmt.label(r.trigger)}</td>
                <td><Pill tone={statusTone(r.status)}>{fmt.label(r.status)}</Pill>{r.error && <div className="muted">{r.error}</div>}</td>
                <td>
                  {r.items_seen} / {r.items_created} / {r.items_updated} / {r.items_removed}
                  {r.status === 'success'
                    && Number(r.items_seen) === 0
                    && Number(r.items_created) === 0
                    && Number(r.items_updated) === 0
                    && Number(r.items_removed) === 0 && (
                    <div className="muted">No assignments were returned in this run.</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <Empty>No sync history.</Empty>}
      <Notices a={a} />
      </>)}
    </section>
  );
}

/* ------------------------------- sports -------------------------------- */

function SportsSetup({ ctx }: { ctx: Ctx }) {
  const { data, studentId, reload } = ctx;
  const sports = useAsync(() => api('GET', `/api/students/${studentId}/sports`), [studentId]);
  const [lookup, setLookup] = React.useState<any>(null);
  const [form, setForm] = React.useState({ sport: '', level: '', teamName: '' });
  // Tracks the dropdown selection separately from the stored sport value so
  // "Other" can reveal a free-text field without losing the picked option.
  const [sportChoice, setSportChoice] = React.useState('');
  const [editingTeamId, setEditingTeamId] = React.useState<string | null>(null);
  const [listItems, setListItems] = React.useState<Record<string, string>>({});
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const schoolName = data.student.school_name ?? '';
  const run = async (fn: () => Promise<any>) => {
    setBusy(true); setError(null);
    try { await fn(); await sports.reload(); await reload(); }
    catch (e: any) { setError(e.message ?? String(e)); }
    finally { setBusy(false); }
  };
  const doLookup = () => run(async () => setLookup(await api('POST', `/api/students/${studentId}/sports/lookup`, {})));
  const resetForm = () => {
    setForm({ sport: '', level: '', teamName: '' });
    setSportChoice('');
    setEditingTeamId(null);
  };
  const saveManualTeam = () => run(async () => {
    if (editingTeamId) {
      await api('PATCH', `/api/students/${studentId}/sports/teams/${editingTeamId}`, {
        sport: form.sport, level: form.level, teamName: form.teamName,
      });
      const lookupResult = await api<any>('POST', `/api/students/${studentId}/sports/lookup`, {});
      setLookup(lookupResult);
      if (lookupResult.status === 'matched') {
        try {
          const result = await api<any>('POST', `/api/students/${studentId}/sports/teams`, {
            sport: form.sport,
            level: form.level || undefined,
            teamName: form.teamName || undefined,
          });
          await api('POST', `/api/students/${studentId}/sports/teams/${result.teamId}/sync`, {});
        } catch (lookupError: any) {
          setLookup({
            ...lookupResult,
            detail: `Team details saved. Automatic schedule lookup did not match: ${lookupError.message ?? String(lookupError)}`,
          });
        }
      }
    } else {
      await api('POST', `/api/students/${studentId}/sports/manual-team`, {
        sport: form.sport,
        level: form.level,
        teamName: form.teamName,
      });
    }
    resetForm();
  });
  if (sports.loading && !sports.data) return <section className="card"><h2>Sports setup</h2><Empty>Loading sports…</Empty></section>;
  if (sports.error) return <section className="card"><h2>Sports setup</h2><ErrorBanner error={sports.error} onRetry={sports.reload} /></section>;
  const profile = sports.data?.profile;
  const providerAvailable = sports.data?.providerAvailable === true;
  const games = sports.data?.games ?? [];
  const teams = sports.data?.teams ?? [];
  return (
    <section className="card" id="student-sports" tabIndex={-1}>
      <div className="spread"><div><h2>Sports setup <Pill tone="warn">Optional</Pill></h2>
        <p className="muted">SMS and voice remain your primary assistant. Add sports here only if it helps.</p></div>
        <button disabled={busy || !providerAvailable} onClick={doLookup}>
          {providerAvailable ? 'Refresh school match' : 'Provider lookup unavailable'}
        </button></div>
      <div className="banner">
        <strong>School match</strong>
        <p style={{ margin: '4px 0' }}>Saved school: <strong>{schoolName || 'No school saved yet'}</strong></p>
        {!providerAvailable ? (
          <p style={{ margin: 0 }}>
            Automatic schedule lookup is not connected yet. Save the team details now; schedule details can be added later.
          </p>
        ) : profile?.arbiter_school_name ? (
          <p style={{ margin: 0 }}>{profile.arbiter_school_name} · {profile.lookup_status}</p>
        ) : (
          <p style={{ margin: 0 }}>
            No confirmed provider match yet. You can still save the team below — we'll keep trying to match the
            schedule automatically, or you can refresh the match and confirm a school once one is available.
          </p>
        )}
      </div>
      {lookup?.detail && <p className="notice" role="status">{lookup.detail}</p>}
      {lookup?.candidates?.length > 0 && (
        <div className="notice"><strong>Choose the school that matches</strong>
          <ul className="plain">{lookup.candidates.map((c: any) => <li key={c.id} className="spread">
            <span>{c.name}</span><button disabled={busy} onClick={() => run(() => api('POST', `/api/students/${studentId}/sports/school`, { schoolId: c.id, schoolName: c.name, schoolUrl: c.url || undefined }))}>Use this school</button>
          </li>)}</ul>
        </div>
      )}
      <h3>{editingTeamId ? 'Edit team' : teams.length ? 'Add another sport or team' : 'Add a team'}</h3>
      <div className="grid">
        <Field
          label="Sport"
          value={sportChoice}
          onChange={(v) => {
            setSportChoice(v);
            if (v !== '__other__') setForm({ ...form, sport: v });
            else setForm({ ...form, sport: '' });
          }}
          options={SPORT_OPTIONS}
        />
        {sportChoice === '__other__' && (
          <Field label="Sport name" value={form.sport} onChange={(v) => setForm({ ...form, sport: v })} placeholder="e.g. Ultimate Frisbee" />
        )}
        <Field label="Team level" value={form.level} onChange={(v) => setForm({ ...form, level: v })} placeholder="Varsity, JV, U14…" />
        <Field label="Team name (optional)" value={form.teamName} onChange={(v) => setForm({ ...form, teamName: v })} placeholder="Tigers" />
      </div>
      <div className="row" style={{ marginTop: 8 }}>
        {!editingTeamId && (
          <button
            className="primary"
            disabled={busy || !form.sport || !form.teamName}
            onClick={saveManualTeam}
          >
            Save team
          </button>
        )}
        {editingTeamId && <button className="primary" disabled={busy || !form.sport || !form.teamName} onClick={saveManualTeam}>Save changes</button>}
        {editingTeamId && <button disabled={busy} onClick={resetForm}>Cancel editing</button>}
      </div>
      {error && <p className="banner bad" role="alert">{error}</p>}
      <h3>Saved teams</h3>
      {teams.length ? (
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>School</th>
                <th>Sport</th>
                <th>Team</th>
                <th>Level</th>
                <th>Schedule</th>
                <th>Notifications</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((t: any) => {
                const providerLinked = t.provider_team_id && !String(t.provider_team_id).startsWith('manual:');
                const hasScheduleLink = Boolean(t.source_url);
                const upcomingCount = games.filter((g: any) => g.team_id === t.id).length;
                const scheduleStatus = upcomingCount
                  ? `${upcomingCount} upcoming`
                  : providerLinked
                    ? 'Linked; no games imported'
                    : hasScheduleLink
                      ? 'Link saved'
                      : 'Not linked';
                return (
                  <tr key={t.id}>
                    <td>{schoolName || 'Not provided'}</td>
                    <td>{t.sport || 'Not provided'}</td>
                    <td>{t.team_name || 'Not provided'}</td>
                    <td>{t.team_level || 'Not provided'}</td>
                    <td>{scheduleStatus}</td>
                    <td>
                      <label className="row" style={{ gap: 6 }}>
                        <input
                          type="checkbox"
                          checked={t.notifications_enabled}
                          disabled={busy}
                          onChange={(e) => run(() => api(
                            'PATCH',
                            `/api/students/${studentId}/sports/teams/${t.id}/preferences`,
                            { notificationsEnabled: e.target.checked },
                          ))}
                        />
                        {t.notifications_enabled ? 'On' : 'Off'}
                      </label>
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        <button disabled={busy} onClick={() => {
                          setEditingTeamId(t.id);
                          setForm({
                            sport: t.sport,
                            level: t.team_level,
                            teamName: t.team_name,
                          });
                          const known = SPORT_OPTIONS.some((o) => o.value === String(t.sport).toLowerCase());
                          setSportChoice(known ? String(t.sport).toLowerCase() : '__other__');
                        }}>Edit</button>
                        <button
                          disabled={busy}
                          onClick={() => run(() => api('DELETE', `/api/students/${studentId}/sports/teams/${t.id}`))}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <Empty>No teams saved yet.</Empty>}
      <h3>Upcoming games</h3>
      {games.length ? <ul className="plain">{games.map((g: any) => <li key={g.id}><strong>{fmt.dateTime(g.starts_at, data.student.timezone)}</strong> · {g.sport} {g.team_name} vs {g.opponent || 'opponent TBD'} · {g.location || 'location TBD'}</li>)}</ul> : <Empty>No upcoming games yet. Sync a provider team when available.</Empty>}
      <h3>Packing lists</h3>
      <p className="muted">Packing items are managed separately for each saved team.</p>
      {teams.length ? <ul className="plain">{teams.map((t: any) => {
        const list = (data.packingLists ?? []).find((l: any) => l.id === t.packing_list_id);
        return <li key={t.id}><strong>{t.sport || 'Not provided'} | {t.team_name || 'Not provided'} | {t.team_level || 'Not provided'}</strong>
          {list ? <><Field label={`Packing items for ${list.name}`} value={listItems[list.id] ?? (list.items ?? []).join(', ')} onChange={(v) => setListItems({ ...listItems, [list.id]: v })} />
            <button disabled={busy} onClick={() => run(() => api('PATCH', `/api/students/${studentId}/packing-lists/${list.id}`, { items: (listItems[list.id] ?? '').split(',').map((x: string) => x.trim()).filter(Boolean) }))}>Save packing items</button></> :
            <span className="muted">Default packing: {(DEFAULT_SPORT_PACKING[String(t.sport).toLowerCase()] ?? ['water bottle', 'uniform']).join(', ')}. Add a custom list only if you need different items.</span>}
        </li>;
      })}</ul> : <p className="muted">Save a team to enable per-sport reminders and packing items.</p>}
    </section>
  );
}

function ClassSchedule({ ctx, canEdit, onSetUpLater }: { ctx: Ctx; canEdit: boolean; onSetUpLater: () => void }) {
  const { studentId, data, reload } = ctx;
  const schedule = useAsync(() => api<any>('GET', `/api/students/${studentId}/class-schedule`), [studentId]);
  const readiness = useAsync(() => api<any>('GET', '/api/school/class-schedule/readiness'), []);
  const action = useAction(() => { void schedule.reload(); reload(); });
  const [feedUrl, setFeedUrl] = React.useState('');
  const [timezone, setTimezone] = React.useState(data.student.timezone);
  const [replaceMode, setReplaceMode] = React.useState(false);
  const [includeMode, setIncludeMode] = React.useState<'all' | 'selected'>('all');
  const [selectedEventIds, setSelectedEventIds] = React.useState<string[]>([]);
  const [calendarSelectionRequired, setCalendarSelectionRequired] = React.useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('calendar') === 'select_required'
      && params.get('tab') === 'class-schedule'
      && window.location.pathname.endsWith(`/students/${studentId}`);
  });
  const [calendarOptions, setCalendarOptions] = React.useState<any[]>([]);
  const [calendarConnectionId, setCalendarConnectionId] = React.useState('');
  const [selectedCalendarId, setSelectedCalendarId] = React.useState('');
  const [calendarLoading, setCalendarLoading] = React.useState(false);
  const [calendarError, setCalendarError] = React.useState<string | null>(null);
  const [calendarLoadAttempt, setCalendarLoadAttempt] = React.useState(0);
  const [feedValidationMessage, setFeedValidationMessage] = React.useState<string | null>(null);
  const [feedValidationBusy, setFeedValidationBusy] = React.useState(false);
  useDraftProtection(canEdit && (Boolean(feedUrl) ||
    timezone !== (schedule.data?.connection?.schedule_timezone ?? data.student.timezone) ||
    (Boolean(schedule.data?.connection) && (includeMode !== schedule.data.connection.include_mode ||
      JSON.stringify(selectedEventIds) !== JSON.stringify(schedule.data.connection.selected_event_ids ?? [])))));

  React.useEffect(() => {
    setTimezone(schedule.data?.connection?.schedule_timezone ?? data.student.timezone);
  }, [data.student.timezone, schedule.data?.connection?.schedule_timezone]);

  React.useEffect(() => {
    setIncludeMode(schedule.data?.connection?.include_mode === 'selected' ? 'selected' : 'all');
    setSelectedEventIds(schedule.data?.connection?.selected_event_ids ?? []);
  }, [schedule.data?.connection?.id, schedule.data?.connection?.source_version]);

  React.useEffect(() => {
    if (!calendarSelectionRequired || !canEdit) return;
    let active = true;
    setCalendarLoading(true);
    setCalendarError(null);
    api<any>('GET', `/api/students/${studentId}/class-schedule/calendars`)
      .then((result) => {
        if (!active) return;
        setCalendarOptions(result.calendars ?? []);
        setCalendarConnectionId(result.connectionId);
        setSelectedCalendarId(result.calendars?.find((item: any) => item.primary)?.id ?? result.calendars?.[0]?.id ?? '');
      })
      .catch((error: unknown) => {
        if (active) setCalendarError(error instanceof Error ? error.message : String(error));
      })
      .finally(() => { if (active) setCalendarLoading(false); });
    return () => { active = false; };
  }, [calendarLoadAttempt, canEdit, calendarSelectionRequired, studentId]);

  if (schedule.loading && !schedule.data) {
    return <section className="card" id="student-class-schedule"><h2>Class Schedule</h2><Loading what="class schedule" /></section>;
  }
  if (schedule.error) {
    return <section className="card" id="student-class-schedule"><h2>Class Schedule</h2><ErrorBanner error={schedule.error} onRetry={schedule.reload} /></section>;
  }

  const connection = schedule.data?.connection;
  const preferences = schedule.data?.preferences ?? { enabled: false, delivery_time: '07:30' };
  const preview = selectedClassEvents(schedule.data?.preview ?? [],
    canEdit ? connection?.include_mode ?? 'all' : 'all', connection?.selected_event_ids ?? []);
  const series = classEventSeries(schedule.data?.preview ?? []);
  const allDayPreview = (schedule.data?.allDayPreview ?? []).filter((event: any) => event.status !== 'cancelled' && event.all_day);
  const linkError = calendarLinkValidationError(feedUrl);
  const coverage = schedule.data?.coverage ?? {
    timedEventCount: preview.length,
    timedWithLocationCount: preview.filter((event: any) => Boolean(event.location)).length,
    timedMissingLocationCount: preview.filter((event: any) => !event.location).length,
    locationCoveragePct: preview.length
      ? Math.round((preview.filter((event: any) => Boolean(event.location)).length / preview.length) * 100)
      : 0,
    allDayEventCount: allDayPreview.length,
  };
  const classSyncStatusLabel = connection?.last_sync_status ? fmt.label(connection.last_sync_status) : 'Not synced yet';
  const classSyncLastLabel = connection?.last_sync_at
    ? fmt.dateTime(connection.last_sync_at, connection.schedule_timezone)
    : 'Not completed yet';
  const outlookConfigured = Boolean(readiness.data?.outlook?.configured);
  const googleConfigured = Boolean(readiness.data?.google?.configured);
  const savePreferences = (next: { enabled?: boolean; deliveryTime?: string }) =>
    action.run(() => api('PATCH', `/api/students/${studentId}/class-schedule/preferences`, next), 'Class reminder settings saved.');
  const beginCalendarAuthorization = (provider: 'outlook_calendar' | 'google_calendar') =>
    action.run(async () => {
      const result = await api<any>('GET', `/api/students/${studentId}/class-schedule/${provider}/authorize-url`);
      window.location.assign(result.url);
    });
  const postClassScheduleSetupStage = React.useCallback(
    async (
      stage: 'feed_connect_started' | 'feed_connect_succeeded' | 'feed_connect_failed' | 'feed_validation_failed',
      errorCode?: string,
    ) => {
      try {
        await api('POST', `/api/students/${studentId}/class-schedule/setup-telemetry`, {
          stage,
          errorCode: errorCode ?? undefined,
        });
      } catch {
        // Telemetry should never block setup progress.
      }
    },
    [studentId],
  );
  const validateFeedLink = async () => {
    const value = feedUrl.trim();
    if (!value || linkError) return;
    setFeedValidationBusy(true);
    setFeedValidationMessage(null);
    try {
      await api('POST', `/api/students/${studentId}/class-schedule/ics/validate`, { feedUrl: value });
      setFeedValidationMessage('Feed link looks valid.');
    } catch (error: any) {
      const errorCode = error instanceof ApiError ? error.code : undefined;
      void postClassScheduleSetupStage('feed_validation_failed', errorCode);
      setFeedValidationMessage(error instanceof Error ? error.message : 'Could not validate this feed link yet.');
    } finally {
      setFeedValidationBusy(false);
    }
  };
  const selectedCalendar = calendarOptions.find((item) => item.id === selectedCalendarId);

  return (
    <section className="card" id="student-class-schedule" tabIndex={-1}>
      <div className="spread">
        <div>
          <h2>Class Schedule <Pill tone="warn">Optional</Pill></h2>
          <p className="muted">Connect a school calendar so the assistant can answer class questions and send a separate daily schedule reminder.</p>
        </div>
        <Pill tone={connection?.last_sync_status === 'success' ? 'ok' : connection?.last_sync_status === 'failed' ? 'bad' : undefined}>
          {calendarConnectionLabel(connection)}
        </Pill>
      </div>

      {connection && !replaceMode ? (
        <>
          <div className="banner">
            <strong>{connection.display_name || connection.external_calendar_name || 'Class calendar'}</strong>
            <div className="muted">
              {connection.provider === 'ics' ? 'School calendar link' : connection.provider} · {connection.schedule_timezone}
              {connection.last_sync_at ? ` · Last sync ${fmt.dateTime(connection.last_sync_at, connection.schedule_timezone)}` : ''}
            </div>
            <div className="field-help">
              Last class sync status: {classSyncStatusLabel} · Last class sync: {classSyncLastLabel}
            </div>
            {!connection.last_sync_at && (
              <div className="field-help">Initial class sync starts automatically after connect and usually completes within about 1-2 minutes.</div>
            )}
          </div>
          {connection.last_sync_status === 'failed' && (
            <div className="banner bad" role="alert">
              <strong>We couldn’t update this calendar.</strong> The saved schedule may be out of date.
              Check that the school link is still active, then refresh or replace it.
            </div>
          )}
          {canEdit && (
            <div className="row" style={{ gap: 8 }}>
              <button
                disabled={action.busy}
                onClick={() => action.run(
                  () => api('POST', `/api/students/${studentId}/class-schedule/sync`),
                  'Class schedule sync requested. Most refreshes finish in about 1-2 minutes.',
                )}
              >
                Sync class schedule now
              </button>
              {canEdit && <button disabled={action.busy} onClick={() => setReplaceMode(true)}>Replace calendar</button>}
              <button className="danger" disabled={action.busy} onClick={() => action.run(() => api('POST', `/api/students/${studentId}/class-schedule/disconnect`), 'Class calendar disconnected.')}>Disconnect</button>
            </div>
          )}
          {canEdit && (
            <section aria-label="Choose class event series">
              <h3>Which calendar events are classes?</h3>
              <p className="muted">Review timed events from this calendar. Selection applies to every occurrence of a series, including future syncs. All-day notices stay separate and never become class reminders.</p>
              <label className="row">
                <input type="radio" name="class-event-mode" checked={includeMode === 'all'} onChange={() => setIncludeMode('all')} />
                Include all timed calendar events (current default)
              </label>
              <label className="row">
                <input type="radio" name="class-event-mode" checked={includeMode === 'selected'} onChange={() => setIncludeMode('selected')} />
                Include only selected event series
              </label>
              {includeMode === 'selected' && (
                <>
                  {series.length ? <ul className="plain">
                    {series.map((event: any) => (
                      <li key={event.source_event_id}>
                        <label className="row">
                          <input type="checkbox" checked={selectedEventIds.includes(event.source_event_id)}
                            onChange={(change) => setSelectedEventIds((ids) => change.target.checked
                              ? [...ids, event.source_event_id] : ids.filter((id) => id !== event.source_event_id))} />
                          {event.title} · next shown {fmt.dateTime(event.starts_at, event.timezone || timezone)}
                        </label>
                      </li>
                    ))}
                  </ul> : <p className="muted">No upcoming timed events in the preview. You can save an empty selection to exclude all timed events.</p>}
                  {selectedEventIds.filter((id) => !series.some((event: any) => event.source_event_id === id)).length > 0 && (
                    <div>
                      <strong>Previously selected series not in this preview</strong>
                      <ul className="plain">
                        {selectedEventIds.filter((id) => !series.some((event: any) => event.source_event_id === id))
                          .map((id, index) => <li key={id}>
                            <label className="row"><input type="checkbox" checked
                              onChange={() => setSelectedEventIds((ids) => ids.filter((value) => value !== id))} />
                              Saved event series {index + 1} (not in current preview)
                            </label>
                          </li>)}
                      </ul>
                    </div>
                  )}
                  <p className="field-help">Only the first 100 upcoming calendar entries are shown. Saved series not shown here stay selected unless you uncheck them.</p>
                </>
              )}
              <button type="button" disabled={action.busy
                || (includeMode === (connection.include_mode ?? 'all')
                  && (includeMode === 'all'
                    || (selectedEventIds.length === (connection.selected_event_ids ?? []).length
                      && selectedEventIds.every((id) => connection.selected_event_ids.includes(id)))))}
                onClick={() => void action.run(() => api('PUT', `/api/students/${studentId}/class-schedule/rules`, {
                  includeMode, selectedEventIds: includeMode === 'selected' ? selectedEventIds : [],
                }), 'Class event selection saved. Pending class reminders were cleared.')}>
                Save class selection
              </button>
            </section>
          )}
        </>
      ) : canEdit ? (
        <>
        <form className="class-schedule-form" onSubmit={async (event) => {
          event.preventDefault();
          await postClassScheduleSetupStage('feed_connect_started');
          const connected = await action.run(
            () => api('POST', `/api/students/${studentId}/class-schedule/ics`, {
              feedUrl, displayName: 'School class schedule', scheduleTimezone: timezone,
            }),
            'Class calendar connected.',
          );
          if (connected?.ok) {
            await postClassScheduleSetupStage('feed_connect_succeeded');
            setReplaceMode(false);
            setFeedUrl('');
            setFeedValidationMessage(null);
          } else {
            await postClassScheduleSetupStage('feed_connect_failed');
            void schedule.reload();
            reload();
          }
        }}>
          <h3>Connect a school calendar link</h3>
          <p className="muted">Paste a school calendar subscription link (WebCal or HTTPS), not the browser address bar. This allows class times, room/location details, and schedule updates.</p>
          <div className="setup-help-box">
            <strong>Bush / MySchoolApp quick path</strong>
            <ol>
              <li>Open <a href="https://bush.myschoolapp.com/" target="_blank" rel="noreferrer">MySchoolApp Calendar</a>.</li>
              <li>Choose the <strong>feed icon</strong> → <strong>WebCal Feed</strong>.</li>
              <li>Under <strong>My Calendars</strong>, copy the <strong>Schedule</strong> link address.</li>
              <li>Paste the copied link below.</li>
            </ol>
            <p className="field-help"><strong>Do not copy the browser address bar.</strong> A valid feed usually includes <span className="mono">webcal://</span> or <span className="mono">https://</span> and often contains <span className="mono">feed</span> or <span className="mono">.ics</span>.</p>
            <details>
              <summary><strong>Other schools</strong></summary>
              <p className="field-help">
                Ask your school portal for an iCal/ICS/WebCal class schedule subscription link.
                If your school offers Outlook or Google calendar sharing instead, use the connector options below.
              </p>
            </details>
          </div>
          <Field label="Calendar name" value="School class schedule" onChange={() => {}} readOnly />
          <Field
            label="School calendar link"
            value={feedUrl}
            onChange={(value) => {
              setFeedUrl(value);
              setFeedValidationMessage(null);
            }}
            placeholder="Paste calendar feed URL"
            required
            helperText="Example: webcal://bush.myschoolapp.com/podium/feed/iCal.aspx?z=..."
            error={linkError ?? undefined}
          />
          <Field label="Schedule timezone" value={timezone} onChange={setTimezone} options={timezoneSelectOptions(timezone)} />
          {feedValidationMessage && (
            <p className={feedValidationMessage === 'Feed link looks valid.' ? 'banner ok' : 'banner bad'} role="status">
              {feedValidationMessage}
            </p>
          )}
          <div className="row class-schedule-actions">
            <button type="button" disabled={feedValidationBusy || action.busy || !feedUrl.trim() || Boolean(linkError)} onClick={() => void validateFeedLink()}>
              {feedValidationBusy ? 'Checking link…' : 'Check link'}
            </button>
            <button className="primary" type="submit" disabled={action.busy || !feedUrl.trim() || Boolean(linkError)}>Connect and preview</button>
            <button type="button" disabled={action.busy} onClick={() => connection ? setReplaceMode(false) : onSetUpLater()}>
              {connection ? 'Keep current calendar' : 'Set up later'}
            </button>
          </div>
        </form>
        {(outlookConfigured || googleConfigured) && (
          <section className="class-schedule-other">
            <h3>Other ways to connect</h3>
            <div className="grid">
              {outlookConfigured && (
                <div>
                  <strong>Microsoft school account</strong>
                  <p className="muted">Read-only calendar access. Some schools require an administrator to approve access. If approval is blocked, use the school calendar link above.</p>
                  <button type="button" disabled={action.busy} onClick={() => void beginCalendarAuthorization('outlook_calendar')}>Connect Microsoft calendar</button>
                </div>
              )}
              {googleConfigured && (
                <div>
                  <strong>Google Calendar</strong>
                  <p className="muted">Read-only calendar access; choose the account and calendar that contain the student’s class sessions.</p>
                  <button type="button" disabled={action.busy} onClick={() => void beginCalendarAuthorization('google_calendar')}>Connect Google calendar</button>
                </div>
              )}
            </div>
          </section>
        )}
        </>
      ) : (
        <p className="muted">A parent can connect a school calendar from this child&apos;s Class Schedule tab.</p>
      )}

      {calendarSelectionRequired && canEdit && (
        <section className="card" aria-live="polite">
          <h3>Select the class calendar</h3>
          <p className="muted">Choose the calendar that contains this student&apos;s class sessions. This selection replaces any previously connected class schedule only after you confirm.</p>
          {calendarLoading ? <Loading what="available calendars" /> : calendarError ? (
            <>
              <p className="banner bad" role="alert">{calendarError}</p>
              <button type="button" disabled={calendarLoading} onClick={() => setCalendarLoadAttempt((attempt) => attempt + 1)}>Retry calendar list</button>
            </>
          ) : calendarOptions.length ? (
            <>
              <label className="field">
                <span>Calendar</span>
                <select value={selectedCalendarId} onChange={(event) => {
                  setSelectedCalendarId(event.target.value);
                  const option = calendarOptions.find((item) => item.id === event.target.value);
                  if (option?.timezone) setTimezone(option.timezone);
                }}>
                  {calendarOptions.map((item) => <option key={item.id} value={item.id}>{item.name}{item.primary ? ' (primary)' : ''}</option>)}
                </select>
              </label>
              <Field label="Schedule timezone" value={timezone} onChange={setTimezone} options={timezoneSelectOptions(timezone)} />
              <button
                className="primary"
                disabled={action.busy || !selectedCalendarId}
                onClick={() => void action.run(
                  () => api('POST', `/api/students/${studentId}/class-schedule/calendars/select`, {
                    connectionId: calendarConnectionId,
                    calendarId: selectedCalendarId,
                    calendarName: selectedCalendar?.name ?? 'Selected calendar',
                    scheduleTimezone: timezone,
                  }),
                  'Calendar selected. Initial sync requested.',
                ).then((result) => {
                  if (result?.ok) {
                    setCalendarSelectionRequired(false);
                    window.history.replaceState({}, '', window.location.pathname);
                    void schedule.reload();
                  }
                })}
              >Confirm calendar</button>
            </>
          ) : <Empty>No calendars were found for this account.</Empty>}
        </section>
      )}

      {connection && (
        <>
          <h3>Daily schedule reminder</h3>
          <div className="grid">
            <label className="row" style={{ gap: 8 }}>
              <input
                type="checkbox"
                checked={Boolean(preferences.enabled)}
                disabled={action.busy || (!canEdit && data.student.parent_visibility !== 'full')}
                onChange={(event) => void savePreferences({ enabled: event.target.checked })}
              />
              Send my class schedule
            </label>
            <Field
              label="Delivery time"
              type="time"
              value={String(preferences.delivery_time ?? '07:30').slice(0, 5)}
              onChange={(value) => void savePreferences({ deliveryTime: value })}
              disabled={action.busy || (!canEdit && data.student.parent_visibility !== 'full')}
            />
          </div>
          <p className="field-help">Default: 7:30 AM in the schedule timezone. This setting is independent of assignment and sports reminders.</p>
        </>
      )}

      <h3>Upcoming classes</h3>
      {connection && (
        <p className="field-help">
          Coverage: {coverage.timedEventCount} timed {coverage.timedEventCount === 1 ? 'class' : 'classes'} synced ·
          {' '}rooms/locations on {coverage.timedWithLocationCount} ({coverage.locationCoveragePct}%) ·
          {' '}{coverage.timedMissingLocationCount} timed without room/location ·
          {' '}{coverage.allDayEventCount} all-day notice{coverage.allDayEventCount === 1 ? '' : 's'}.
        </p>
      )}
      {preview.length ? (
        <ul className="plain">
          {preview.map((event: any) => (
            <li key={`${event.source_event_id}:${event.occurrence_key}`}>
              <strong>{fmt.dateTime(event.starts_at, event.timezone || timezone)}</strong> · {event.title}
              {event.location ? ` · ${event.location}` : ''}
            </li>
          ))}
        </ul>
      ) : <Empty>{connection?.include_mode === 'selected'
        ? 'No upcoming timed classes match the saved parent selection.'
        : calendarEmptyPreviewMessage(connection)}</Empty>}
      {allDayPreview.length > 0 && (
        <>
          <h3>All-day calendar notices</h3>
          <p className="field-help">These calendar entries do not have a class time, so they are not shown as midnight classes or included in daily class reminders.</p>
          <ul className="plain">
            {allDayPreview.map((event: any) => (
              <li key={`${event.source_event_id}:${event.occurrence_key}`}>
                <strong>{fmt.date(event.starts_at, event.timezone || timezone)}</strong> · {event.title}
              </li>
            ))}
          </ul>
        </>
      )}
      <Notices a={action} />
    </section>
  );
}

/* ------------------------------ messages ------------------------------ */

type VoiceSessionResponse = {
  sessionId: string;
  sessionToken: string;
  expiresAt: string;
  transport: { path: string };
  provider: VoiceProviderId;
  audio: { inputFormat: string; outputFormat: string; sampleRateHz: number };
};

type VoiceProviderId = 'azure_voice_live' | 'xai_grok' | 'openai_realtime';
type VoiceProviderOption = {
  id: VoiceProviderId;
  label: string;
  available: boolean;
  reason: string | null;
};

function pcm16FromFloat(input: Float32Array<ArrayBufferLike>, sourceRate: number, targetRate: number): ArrayBuffer {
  const ratio = sourceRate / targetRate;
  const length = Math.max(1, Math.round(input.length / ratio));
  const output = new ArrayBuffer(length * 2);
  const view = new DataView(output);
  for (let i = 0; i < length; i += 1) {
    const position = i * ratio;
    const left = Math.floor(position);
    const right = Math.min(left + 1, input.length - 1);
    const weight = position - left;
    const sample = Math.max(-1, Math.min(1, input[left] * (1 - weight) + input[right] * weight));
    view.setInt16(i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
  return output;
}

function base64ToPcm16(base64: string): Float32Array {
  const bytes = Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const samples = new Float32Array(Math.floor(bytes.byteLength / 2));
  for (let i = 0; i < samples.length; i += 1) samples[i] = view.getInt16(i * 2, true) / 0x8000;
  return samples;
}

function VoiceConversation({ ctx }: { ctx: Ctx }) {
  const { studentId } = ctx;
  const readiness = useAsync(() => api<{
    enabled: boolean; ready: boolean; reason?: string | null;
    consentReady?: boolean;
    providerSelection?: {
      enabled: boolean;
      defaultProvider: VoiceProviderId;
      options: VoiceProviderOption[];
    };
  }>('GET', `/api/students/${studentId}/voice/readiness`), [studentId]);
  const [status, setStatus] = React.useState<'idle' | 'starting' | 'listening' | 'speaking' | 'error'>('idle');
  const [selectedProvider, setSelectedProvider] = React.useState<VoiceProviderId>('azure_voice_live');
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const sessionIdRef = React.useRef<string | null>(null);
  const [muted, setMuted] = React.useState(false);
  const socketRef = React.useRef<WebSocket | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const inputContextRef = React.useRef<AudioContext | null>(null);
  const outputContextRef = React.useRef<AudioContext | null>(null);
  const inputSourceRef = React.useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = React.useRef<ScriptProcessorNode | null>(null);
  const nextAudioAtRef = React.useRef(0);
  const outputSourcesRef = React.useRef<AudioBufferSourceNode[]>([]);
  const providerSelection = readiness.data?.providerSelection;
  const selectedProviderOption = providerSelection?.options.find((option) => option.id === selectedProvider);
  const voiceReady = providerSelection?.enabled
    ? Boolean(readiness.data?.consentReady && selectedProviderOption?.available)
    : Boolean(readiness.data?.ready);
  const readinessReason = providerSelection?.enabled
    ? selectedProviderOption?.reason
      ?? (readiness.data?.consentReady ? 'No configured browser voice provider is available.' : readiness.data?.reason)
    : readiness.data?.reason;

  React.useEffect(() => {
    if (!providerSelection?.enabled) return;
    const selectedOption = providerSelection.options.find((option) => option.id === selectedProvider);
    if (selectedOption?.available) return;
    const availableProvider = providerSelection.options.find(
      (option) => option.id === providerSelection.defaultProvider && option.available,
    ) ?? providerSelection.options.find((option) => option.available);
    if (availableProvider) setSelectedProvider(availableProvider.id);
  }, [providerSelection, selectedProvider]);

  const stopPlayback = React.useCallback(() => {
    outputSourcesRef.current.forEach((source) => {
      try { source.stop(); } catch { /* already stopped */ }
    });
    outputSourcesRef.current = [];
    nextAudioAtRef.current = 0;
  }, []);

  const cleanup = React.useCallback(async (endSession = true) => {
    processorRef.current?.disconnect();
    inputSourceRef.current?.disconnect();
    inputContextRef.current?.close();
    outputContextRef.current?.close();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    socketRef.current?.close(1000, 'voice session ended');
    const activeSessionId = sessionIdRef.current;
    if (endSession && activeSessionId) {
      try { await api('POST', `/api/students/${studentId}/voice/sessions/${activeSessionId}/end`, {}); } catch { /* socket close already ends it */ }
    }
    processorRef.current = null;
    inputSourceRef.current = null;
    inputContextRef.current = null;
    outputContextRef.current = null;
    streamRef.current = null;
    socketRef.current = null;
    sessionIdRef.current = null;
    setMuted(false);
    stopPlayback();
  }, [studentId, stopPlayback]);

  const playAudio = React.useCallback((data: string, sampleRate: number) => {
    const context = outputContextRef.current;
    if (!context) return;
    const samples = base64ToPcm16(data);
    const buffer = context.createBuffer(1, samples.length, sampleRate);
    buffer.getChannelData(0).set(samples);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    const startAt = Math.max(context.currentTime + 0.02, nextAudioAtRef.current);
    source.start(startAt);
    nextAudioAtRef.current = startAt + buffer.duration;
    outputSourcesRef.current.push(source);
    source.onended = () => {
      outputSourcesRef.current = outputSourcesRef.current.filter((item) => item !== source);
    };
    setStatus('speaking');
  }, []);

  const start = async () => {
    setStatus('starting');
    setError(null);
    setNotice(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error('This browser does not support microphone access.');
      const session = await api<VoiceSessionResponse>(
        'POST',
        `/api/students/${studentId}/voice/sessions`,
        providerSelection?.enabled ? { provider: selectedProvider } : {},
      );
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
      const inputContext = new AudioContext();
      const outputContext = new AudioContext();
      await Promise.all([inputContext.resume(), outputContext.resume()]);
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const socket = new WebSocket(`${protocol}//${window.location.host}${session.transport.path}`);
      socket.binaryType = 'arraybuffer';
      socket.onopen = () => socket.send(JSON.stringify({ type: 'authenticate', sessionToken: session.sessionToken }));
      socket.onmessage = (event) => {
        if (typeof event.data !== 'string') return;
        const message = JSON.parse(event.data) as { type?: string; data?: string; code?: string; message?: string };
        if (message.type === 'ready') {
          setStatus('listening');
          setNotice('Listening. Speak naturally, then pause when you are finished.');
        } else if (message.type === 'audio' && message.data) {
          playAudio(message.data, session.audio.sampleRateHz);
        } else if (message.type === 'speech_started') {
          stopPlayback();
          setStatus('listening');
        } else if (message.type === 'audio.done') {
          setStatus('listening');
        } else if (message.type === 'error') {
          setStatus('error');
          setError(message.message ?? 'Voice service reported an error. Try again.');
        }
      };
      socket.onerror = () => {
        setStatus('error');
        setError('The voice connection could not be established. Check your connection and try again.');
      };
      socket.onclose = (event) => {
        if (event.code !== 1000) {
          setStatus('idle');
          setError('The voice session ended. Start a new session to try again.');
        }
      };
      const source = inputContext.createMediaStreamSource(stream);
      const processor = inputContext.createScriptProcessor(4096, 1, 1);
      processor.onaudioprocess = (event) => {
        if (muted || socket.readyState !== WebSocket.OPEN) return;
        const pcm = pcm16FromFloat(event.inputBuffer.getChannelData(0), inputContext.sampleRate, session.audio.sampleRateHz);
        socket.send(pcm);
      };
      source.connect(processor);
      processor.connect(inputContext.destination);
      socketRef.current = socket;
      streamRef.current = stream;
      inputContextRef.current = inputContext;
      outputContextRef.current = outputContext;
      inputSourceRef.current = source;
      processorRef.current = processor;
      sessionIdRef.current = session.sessionId;
    } catch (e) {
      setStatus('error');
      setError(e instanceof Error ? e.message : String(e));
      await cleanup(false);
    }
  };

  const stop = async () => {
    await cleanup(true);
    setStatus('idle');
    setNotice(null);
  };

  const interrupt = () => {
    stopPlayback();
    if (socketRef.current?.readyState === WebSocket.OPEN) socketRef.current.send(JSON.stringify({ type: 'interrupt' }));
    setStatus('listening');
  };

  React.useEffect(() => () => { void cleanup(true); }, [cleanup]);

  return (
    <section className="card voice-card" aria-labelledby="voice-heading">
      <div className="spread">
        <div>
          <h2 id="voice-heading" className="section-title">Talk to your assistant</h2>
          <p className="muted" style={{ margin: 0 }}>
            Tap start, speak your question, and listen to the answer. Your voice transcript is not saved as message text.
          </p>
        </div>
        <Pill tone={status === 'error' ? 'bad' : status === 'idle' ? undefined : 'ok'}>
          {status === 'idle' ? 'Ready' : status === 'starting' ? 'Starting' : status === 'speaking' ? 'Speaking' : status === 'error' ? 'Needs attention' : 'Listening'}
        </Pill>
      </div>
      <div className="voice-controls">
        {providerSelection?.enabled && (
          <label className="field voice-provider-select">
            <span>Voice provider (local comparison)</span>
            <select
              aria-label="Voice provider"
              value={selectedProvider}
              onChange={(event) => {
                const option = providerSelection.options.find((item) => item.id === event.target.value);
                if (option) setSelectedProvider(option.id);
              }}
              disabled={status !== 'idle' && status !== 'error'}
            >
              {providerSelection.options.map((option) => (
                <option key={option.id} value={option.id} disabled={!option.available}>
                  {option.label}{option.available ? '' : ' — not configured'}
                </option>
              ))}
            </select>
          </label>
        )}
        {status === 'idle' || status === 'error' ? (
          <button className="primary voice-start" type="button" disabled={readiness.loading || !voiceReady} onClick={() => void start()}>
            {readiness.loading ? 'Checking voice…' : status === 'error' ? 'Try voice again' : 'Start voice'}
          </button>
        ) : (
          <>
            <button className="primary voice-start" type="button" onClick={() => setMuted((value) => !value)}>
              {muted ? 'Unmute microphone' : 'Mute microphone'}
            </button>
            <button type="button" onClick={interrupt} disabled={status === 'starting'}>Stop speaking</button>
            <button className="danger" type="button" onClick={() => void stop()}>End session</button>
          </>
        )}
      </div>
      {providerSelection?.enabled && (
        <p className="field-help">
          Browser comparison sends your microphone audio to the selected provider for transcription and spoken replies.
          Use only with parent/guardian permission and voice-processing consent. Phone calls currently use OpenAI Realtime; Azure Voice Live is retained for rollback.
        </p>
      )}
      {notice && <p className="banner ok" role="status">{notice}</p>}
      {error && <p className="banner bad" role="alert">{error}</p>}
      {!readiness.loading && readiness.data && !voiceReady && (
        <p className="banner" role="status">{readinessReason}</p>
      )}
      {readiness.error && <p className="banner bad" role="alert">Unable to check voice readiness: {readiness.error}</p>}
      <p className="field-help">
        Voice uses your microphone while this session is active. You can end it at any time; SMS remains available as a fallback.
      </p>
    </section>
  );
}

function Messages({ ctx, canEdit }: { ctx: Ctx; canEdit: boolean }) {
  const { data, studentId, reload } = ctx;
  const a = useAction(reload);
  const [text, setText] = React.useState('');
  const [expanded, setExpanded] = React.useState(true);
  const showDeliveryStatus = !canEdit || data.student.parent_visibility === 'full';

  return (<>
    <section className="card">
      <div className="spread">
        <div>
          <h2 className="section-title">
            <span className="illustration-wrap" aria-hidden="true"><ChatBubbleIllustration title="Chat bubble" /></span>
            Messages
          </h2>
          <p className="muted" style={{ margin: 0 }}>Optional history of the student&apos;s text conversation.</p>
        </div>
        <button onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
          {expanded ? 'Hide messages' : 'Show messages'}
        </button>
      </div>
      {expanded && (<>
      {!data.messagesVisible && (
        <p className="field-help" style={{ margin: '10px 0' }}>
          Message text is hidden by the current parent visibility setting. Receipt and processing status remain visible.
        </p>
      )}
      {data.messages?.length ? (
        <div>
          {[...data.messages].reverse().map((m: any) => (
            <div key={m.id} className={`msg ${m.direction === 'outbound' ? 'out' : 'in'}`}>
              {m.body ? (
                <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
              ) : (
                <div className="muted">
                  {m.direction === 'outbound'
                    ? 'Assistant message sent'
                    : m.channel === 'email' ? 'Inbound email received' : 'Message received'}
                </div>
              )}
              <div className="muted">
                {m.direction === 'outbound' ? 'Student Assistant' : 'student'} · {fmt.dateTime(m.created_at, data.student.timezone)}
                {m.intent ? ` · understood as ${fmt.label(m.intent)}` : ''}
                {m.simulated ? ' · simulated' : ''}
                {showDeliveryStatus && m.status && m.status !== 'viewed' ? ` · ${fmt.label(m.status)}` : ''}
              </div>
            </div>
          ))}
        </div>
      ) : <Empty>No messages yet.</Empty>}

      {data.devTools && (
        <form
          className="grid"
          style={{ alignItems: 'end', marginTop: 10 }}
          onSubmit={async (e) => {
            e.preventDefault();
            await a.run(() => api('POST', `/api/students/${studentId}/simulate-inbound`, { body: text }), 'Processed.');
            setText('');
          }}
        >
          <Field label="Simulate an inbound reply (development only)" value={text} onChange={setText}
            placeholder="I finished my math homework" />
          <button type="submit" disabled={a.busy || !text}>Send as student</button>
        </form>
      )}
      <Notices a={a} />
      </>)}
    </section>
  </>);
}

/* ------------------------------ settings ------------------------------ */

function Settings({
  ctx, canEdit, section = 'settings', saveLabel = 'Save settings', onSaved,
}: { ctx: Ctx; canEdit: boolean; section?: string; saveLabel?: string; onSaved?: () => void }) {
  const { data, studentId, reload } = ctx;
  const a = useAction(reload);
  const s = data.student;
  const [f, setF] = React.useState({
    afterSchoolPlanTime: s.after_school_plan_time?.slice(0, 5) ?? '16:00',
    eveningPackTime: s.evening_pack_time?.slice(0, 5) ?? '20:00',
    schoolDepartureTime: s.school_departure_time?.slice(0, 5) ?? '07:30',
    quietHoursStart: s.quiet_hours_start?.slice(0, 5) ?? '21:00',
    quietHoursEnd: s.quiet_hours_end?.slice(0, 5) ?? '07:00',
    dailyMessageLimit: String(s.daily_message_limit ?? 6),
    parentVisibility: s.parent_visibility ?? 'summary',
    timezone: s.timezone,
  });
  const [phone, setPhone] = React.useState('');
  const [code, setCode] = React.useState('');
  const [contactId, setContactId] = React.useState<string | null>(() => {
    const contact = primarySmsContact(data);
    return contact && !contact.verifiedAt && !contact.optedOutAt ? contact.id : null;
  });
  const [devCode, setDevCode] = React.useState<string | null>(null);
  const [invite, setInvite] = React.useState<string | null>(null);
  const [copiedInvite, setCopiedInvite] = React.useState(false);
  const [copyInviteError, setCopyInviteError] = React.useState<string | null>(null);
  const [copiedAlias, setCopiedAlias] = React.useState(false);
  const [copyAliasError, setCopyAliasError] = React.useState<string | null>(null);
  const [requestedPhone, setRequestedPhone] = React.useState('');
  const [verificationError, setVerificationError] = React.useState<string | null>(null);
  const [verifyingCode, setVerifyingCode] = React.useState(false);
  const codeInputRef = React.useRef<HTMLInputElement>(null);
  const currentSmsContact = primarySmsContact(data);
  const phoneIsVerified = Boolean(currentSmsContact?.verifiedAt && currentSmsContact?.consentAt
    && !currentSmsContact?.optedOutAt && currentSmsContact?.status === 'subscribed');
  const [saved, setSaved] = React.useState(JSON.stringify(f));
  useDraftProtection(JSON.stringify(f) !== saved || Boolean(phone || code));

  // Once a code is sent, move focus straight to the code field so the parent
  // doesn't have to hunt for where to type it after seeing the "code sent"
  // notice at the top of the page.
  React.useEffect(() => {
    if (contactId) codeInputRef.current?.focus();
  }, [contactId]);


  const updatePhone = (value: string) => {
    setPhone(value);
    if (value !== requestedPhone) {
      setContactId(null);
      setCode('');
      setDevCode(null);
      setVerificationError(null);
    }
  };

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    a.run(() => api('PATCH', `/api/students/${studentId}`, {
      ...f, dailyMessageLimit: Number(f.dailyMessageLimit),
    }), 'Saved and reminders recalculated.').then((res) => {
      if (res && res.ok !== false) {
        setSaved(JSON.stringify(f));
        onSaved?.();
      }
    });
  };

  return (
    <section className="card" id="student-settings" tabIndex={-1}>
      <h2 className="section-title">
        <span className="illustration-wrap" aria-hidden="true"><ShieldHeartIllustration title="Shield" /></span>
        {section === 'phone' ? 'Student phone' : section === 'email' ? 'Email forwarding' : 'Schedule, privacy and student access'}
      </h2>
      <p className="muted">
        These settings apply to <strong>{s.preferred_name}</strong> only. Use this page to update this student&apos;s phone, schedule, email alias, and parent message visibility.
      </p>

      {/* Rendered near the top (not the bottom of this long form) so a success/error
          message from any action below -- phone verification, alias confirmation,
          saving settings -- is immediately visible without scrolling. */}
      <Notices a={a} />

      <div hidden={section !== 'email'} id="email-alias-section" tabIndex={-1} className="settings-anchor-section">
      <h3>Email alias for forwarded school messages</h3>
      <div className="banner">
        {s.student_email_alias ? (
          <>
            <strong>Assistant email address:</strong> <span className="mono">{s.student_email_alias}</span>{' '}
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(s.student_email_alias);
                  setCopiedAlias(true);
                  setCopyAliasError(null);
                } catch (error) {
                  setCopyAliasError(error instanceof Error ? `Could not copy email address: ${error.message}` : 'Could not copy email address.');
                }
              }}
            >
              {copiedAlias ? 'Copied' : 'Copy email address'}
            </button>
            <br /><span className="field-help">
              {s.student_email_alias_verified
                ? 'Forward school emails here when you want the assistant to turn them into reviewable tasks or activities.'
                : 'School messages are ignored until you confirm this address and allow email intake.'}
            </span>
            <p className="field-help">
              Example: forward a teacher email about "Bring goggles for Friday lab" to this address. The assistant saves a draft activity for you to review before it affects reminders.
            </p>
            {!s.student_email_alias_verified && (
              <button
                type="button"
                style={{ marginTop: 8 }}
                disabled={a.busy}
                onClick={async () => {
                  await a.run(
                    () => api('POST', `/api/students/${studentId}/email-alias/confirm`, {}),
                    'Email alias confirmed; inbound school email is enabled.',
                  );
                }}
              >
                Confirm alias and enable email intake
              </button>
            )}
          </>
        ) : (
          <>
            <strong>Assistant email address:</strong> not created yet.
            <br /><span className="field-help">Create one so you can forward school emails into this child's assistant. The address is generated automatically.</span>
            <button
              type="button"
              style={{ marginTop: 8 }}
              disabled={a.busy}
              onClick={() => a.run(
                () => api('POST', `/api/students/${studentId}/email-alias/provision`, {}),
                'Email alias created. Confirm it to enable inbound school email.',
              )}
            >
              Create email alias
            </button>
          </>
        )}
      </div>
      {copyAliasError && <p className="banner bad" role="alert">{copyAliasError}</p>}
      </div>

      <div hidden={section !== 'phone'} id="phone-verification-section" tabIndex={-1} className="settings-anchor-section">
      <h3>Student phone verification</h3>
      <p className="muted">
        First verify the current student phone. Only use Change student phone if the number below is wrong.
      </p>
      <TutorialVideo
        title="Verify a student's phone"
        description="See the one-time code steps. The phone number and simulated code in this demo are fictional."
        videoSrc="/tutorials/verify-phone.mp4"
        posterSrc="/tutorials/verify-phone-poster.jpg"
        captionsSrc="/tutorials/verify-phone.vtt"
      />
      <div className={phoneIsVerified ? 'banner ok' : 'banner'}>
        <div className="spread phone-status-row">
          <div>
            <strong>Current student phone:</strong>{' '}
            {currentSmsContact ? <span>{currentSmsContact.destination}</span> : <span>none added yet</span>}
            <div className="field-help">
              {currentSmsContact?.optedOutAt
                ? 'Opted out. This phone cannot receive reminders. Verification must not be used to bypass STOP.'
                : phoneIsVerified
                ? 'Verified. Reminders can be sent to this number.'
                : 'Not verified yet. Send a code to this phone, then enter the code below.'}
            </div>
          </div>
          {currentSmsContact && !phoneIsVerified && !currentSmsContact.optedOutAt && (
            <button
              type="button"
              disabled={a.busy}
              onClick={async () => {
                const res = await a.run(
                  () => api('POST', `/api/students/${studentId}/contacts/${currentSmsContact.id}/verification`, {}),
                  `Verification code sent to ${currentSmsContact.destination}. Enter it in the "Code sent to" field below.`,
                );
                if (res?.contactId) {
                  setContactId(res.contactId);
                  setRequestedPhone(currentSmsContact.destination);
                  setDevCode(res.devCode ?? null);
                  setCode('');
                  setVerificationError(null);
                }
              }}
            >
              {a.busy ? 'Sending code…' : 'Send code to this phone'}
            </button>
          )}
        </div>
        <details className="optional-profile-details">
          <summary>Change student phone</summary>
          <p className="field-help">
            Use this only if the current phone is wrong or the student changed devices. Sending a code replaces the current phone;
            reminders stop for it until the new phone is verified.
          </p>
          <div className="grid" style={{ alignItems: 'end' }}>
            <Field label="New student phone number" type="tel" value={phone} onChange={updatePhone} placeholder="e.g. (206) 555-0123" />
            <button disabled={a.busy || !phone} onClick={async () => {
              const res = await a.run(() => api('POST', `/api/students/${studentId}/contacts`,
                { channel: 'sms', destination: phone, deviceNote: 'added from Command Center' }));
              if (res?.contactId) {
                setContactId(res.contactId);
                setRequestedPhone(phone);
                setDevCode(res.devCode ?? null);
                setCode('');
                setVerificationError(null);
              }
            }}>{a.busy ? 'Sending code…' : 'Send code to new phone'}</button>
          </div>
        </details>
        {contactId && (
          <div className="grid highlight-target" style={{ alignItems: 'end', marginTop: 12 }}>
            <Field label={`Verification code for ${requestedPhone || currentSmsContact?.destination || 'student phone'}`} value={code} onChange={setCode} inputRef={codeInputRef} />
            {verificationError && (
              <p className="banner bad field-span" role="alert" style={{ margin: 0 }}>
                <strong>Could not verify this code.</strong><br />
                {verificationError}
              </p>
            )}
            {/* Compare by contact ID, not by destination text: the server always returns the
                phone number masked (e.g. +1•••••1234), so a freshly-typed raw number from
                "Change student phone" never matches it and the checkmark would immediately
                revert to "Verify code" after the post-verification reload. */}
            <button className="primary" disabled={a.busy || verifyingCode || !code || (phoneIsVerified && currentSmsContact?.id === contactId)}
              onClick={async () => {
                setVerifyingCode(true);
                setVerificationError(null);
                a.setError(null);
                a.setNote(null);
                try {
                  const res = await api('POST', `/api/students/${studentId}/contacts/${contactId}/verify`, { code });
                  if (res?.ok) {
                    a.setNote(`Verified ${requestedPhone || 'student phone'}.`);
                    setCode('');
                    setPhone('');
                    setContactId(null);
                    setDevCode(null);
                    ctx.reload();
                  } else {
                    setVerificationError('That code did not match or has expired. Check the latest text message, or send a new code to this phone.');
                  }
                } catch (error) {
                  setVerificationError(phoneVerificationErrorMessage(error));
                } finally {
                  setVerifyingCode(false);
                }
              }}>
              {a.busy || verifyingCode ? 'Verifying…' : phoneIsVerified && currentSmsContact?.id === contactId ? 'Verified ✓' : 'Verify code'}
            </button>
            {devCode && <p className="banner">SMS is simulated in this environment. The code is <strong>{devCode}</strong>.</p>}
          </div>
        )}
      </div>
      <button disabled={a.busy || verifyingCode} onClick={() => { setPhone(''); setCode(''); setDevCode(null); setContactId(null); setVerificationError(null); }}>Cancel phone entry</button>
      <p className="field-help">Cancel clears this entry, not a phone change already saved on the server. If a code expires or you reload, send a new code to the current phone. An opted-out phone cannot receive reminders.</p>
      </div>

      <div hidden={section !== 'settings'}>
      <h3>Student school sign-in link</h3>
      <div className="row">
        <button disabled={a.busy} onClick={async () => {
          const res = await a.run(() => api('POST', `/api/students/${studentId}/invite`, {}));
          if (res?.url || res?.setupUrl) {
            setInvite(res.url ?? res.setupUrl);
            setCopiedInvite(false);
            setCopyInviteError(null);
          }
        }}>Create sign-in link for this student</button>
        {invite && (
          <>
            <span className="mono" style={{ wordBreak: 'break-all' }}>{invite}</span>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(invite);
                  setCopiedInvite(true);
                  setCopyInviteError(null);
                } catch (error) {
                  setCopyInviteError(error instanceof Error ? `Could not copy sign-in link: ${error.message}` : 'Could not copy sign-in link.');
                }
              }}
            >
              {copiedInvite ? 'Copied' : 'Copy link'}
            </button>
          </>
        )}
      </div>
      {copyInviteError && <p className="banner bad" role="alert">{copyInviteError}</p>}
      <p className="muted">
        This is for <strong>{s.preferred_name}</strong> to sign in to their own student setup page (including Canvas sign-in when available). It does not add a new child.
        To add a different child, go back to Family and use Add another child (new student).
      </p>

      <form onSubmit={save}>
        <h3>Schedule and notifications</h3>
        <p className="field-help" style={{ marginTop: 0 }}>
          Privacy control: use "Message history visible to parent" below to choose Full, Summary, or Status-only visibility.
        </p>
        <div className="grid">
          <Field label="Timezone" value={f.timezone} onChange={(v) => setF({ ...f, timezone: v })}
            options={timezoneSelectOptions(f.timezone)} />
          <Field label="Leaves for school at" type="time" value={f.schoolDepartureTime} onChange={(v) => setF({ ...f, schoolDepartureTime: v })} />
          <Field label="After-school planning" type="time" value={f.afterSchoolPlanTime} onChange={(v) => setF({ ...f, afterSchoolPlanTime: v })} />
          <Field label="Evening packing" type="time" value={f.eveningPackTime} onChange={(v) => setF({ ...f, eveningPackTime: v })} />
          <Field label="Quiet hours start" type="time" value={f.quietHoursStart} onChange={(v) => setF({ ...f, quietHoursStart: v })} />
          <Field label="Quiet hours end" type="time" value={f.quietHoursEnd} onChange={(v) => setF({ ...f, quietHoursEnd: v })} />
          <Field label="Daily message limit" type="number" value={f.dailyMessageLimit} onChange={(v) => setF({ ...f, dailyMessageLimit: v })} />
          <Field label="Message history visible to parent" value={f.parentVisibility} onChange={(v) => setF({ ...f, parentVisibility: v })}
            options={[
              { value: 'full', label: 'Full message contents' },
              { value: 'summary', label: 'Activity and task data; no message text' },
              { value: 'status_only', label: 'Assistant status only; no message history' },
            ]} />
        </div>
        <p className="field-help">
          Current selection: {VISIBILITY_LABEL[f.parentVisibility] ?? 'Activity and task data; no message text'}.
          {f.parentVisibility === 'full'
            ? ' Parents can review message contents for this child.'
            : f.parentVisibility === 'status_only'
              ? ' Parents see assistant status only, not task details or message history.'
              : ' Parents see activity and task details, but not message text.'}
        </p>
        {canEdit && <div className="row" style={{ marginTop: 10 }}>
          <button className="primary" type="submit" disabled={a.busy}>{saveLabel}</button>
          <button type="button" disabled={a.busy} onClick={() => setF(JSON.parse(saved))}>Cancel changes</button>
        </div>}
      </form>
      </div>
    </section>
  );
}

/* -------------------------------- view -------------------------------- */

function Profile({ ctx }: { ctx: Ctx }) {
  const s = ctx.data.student;
  const initial = { preferredName: s.preferred_name, grade: String(s.grade), timezone: s.timezone, schoolName: s.school_name ?? '' };
  const [form, setForm] = React.useState(initial);
  const [saved, setSaved] = React.useState(JSON.stringify(initial));
  const a = useAction(ctx.reload);
  useDraftProtection(JSON.stringify(form) !== saved);
  return <section className="card">
    <h2>Child profile</h2>
    <p className="muted">Only {s.preferred_name}'s profile is being edited. Phone verification has its own destination.</p>
    <form onSubmit={async (event) => {
      event.preventDefault();
      const result = await a.run(() => api('PATCH', `/api/students/${ctx.studentId}`, { ...form, grade: Number(form.grade) }), 'Child profile saved.');
      if (result && result.ok !== false) setSaved(JSON.stringify(form));
    }}>
      <div className="grid">
        <Field label="Preferred name" value={form.preferredName} onChange={(value) => setForm({ ...form, preferredName: value })} required />
        <Field label="Grade" value={form.grade} onChange={(value) => setForm({ ...form, grade: value })}
          options={[5,6,7,8,9,10,11,12].map((grade) => ({ value: String(grade), label: `Grade ${grade}` }))} />
        <Field label="Timezone" value={form.timezone} onChange={(value) => setForm({ ...form, timezone: value })} options={timezoneSelectOptions(form.timezone)} />
        <Field label="School name" value={form.schoolName} onChange={(value) => setForm({ ...form, schoolName: value })} />
      </div>
      <div className="row" style={{ marginTop: 12 }}>
        <button className="primary" disabled={a.busy} type="submit">Save profile</button>
        <button disabled={a.busy} type="button" onClick={() => setForm(JSON.parse(saved))}>Cancel changes</button>
      </div>
    </form>
    <Notices a={a} />
  </section>;
}

function timeGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

const VISIBILITY_LABEL: Record<string, string> = {
  full: 'the full text of messages',
  summary: 'activity and task data, not message text',
  status_only: 'assistant status only, not message history',
};

export function StudentView({ studentId, me, onChanged }: { studentId: string; me: Me; onChanged: () => void | Promise<void> }) {
  const { data, error, loading, reload } = useAsync(() => api('GET', `/api/students/${studentId}`), [studentId]);
  const canEdit = me.principal?.role === 'parent';
  const [location, setLocation] = React.useState(() => childLocation(window.location.search, canEdit));
  React.useEffect(() => {
    const onPop = () => {
      if (window.location.pathname === `/students/${studentId}`) setLocation(childLocation(window.location.search, canEdit));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [studentId, canEdit]);
  const tab = location.section;
  const showMessagesTab = Boolean(data?.messagesVisible);
  const toolGroups = [
    {
      title: 'Core setup',
      description: 'Required identity and schoolwork connections.',
      items: [
        ...(canEdit ? [
          { id: 'school', label: 'Assignments: Canvas connection', description: 'Connect or repair Canvas. This powers tasks, homework, grades, and reminders.' },
          { id: 'phone', label: 'Student phone/watch', description: 'Verify or change the number that receives SMS and can be trusted for voice calls.' },
          { id: 'profile', label: 'Child profile', description: 'Edit name, grade, school, timezone, and daily schedule preferences.' },
        ] : []),
      ],
    },
    {
      title: 'Optional tools',
      description: 'Extra sources beyond Canvas assignments.',
      items: [
        { id: 'class-schedule', label: 'Class schedule calendar', description: 'Optional: connect class times so questions like "When is math?" work.' },
        ...(canEdit ? [{ id: 'sports', label: 'Sports schedules', description: 'Optional: add teams, games, packing lists, and game-day reminders.' }] : []),
      ],
    },
    {
      title: 'Talk-review',
      description: 'Ways the child communicates with the assistant and what parents can review.',
      items: [
        { id: 'voice', label: 'Browser voice', description: 'Talk to the assistant in this web app using the microphone.' },
        ...(showMessagesTab ? [{ id: 'messages', label: 'Messages', description: 'Review visible conversations with the assistant.' }] : []),
        ...(canEdit ? [{ id: 'email', label: 'Email forwarding', description: 'Optional: process forwarded school emails into activity requests.' }] : []),
      ],
    },
    {
      title: 'Advanced controls',
      description: 'Permissions, lifecycle, and reminder settings.',
      items: [
        ...(canEdit ? [
          { id: 'permissions', label: 'Family permissions', description: 'Review data, AI, voice, and live-SMS approvals.' },
          { id: 'settings', label: 'Assistant settings', description: 'Manage quiet hours, reminder times, visibility, and notification preferences.' },
          { id: 'assistant', label: 'Assistant lifecycle', description: 'Pause, resume, restore, unregister, or activate this assistant.' },
        ] : []),
      ],
    },
  ].map((group) => ({ ...group, items: group.items.filter(Boolean) }));
  const moreSections = toolGroups.flatMap((group) => group.items);
  const lastNavigationFocus = React.useRef<string | null>(null);

  React.useEffect(() => {
    const t = setInterval(reload, 15000);
    return () => clearInterval(t);
  }, [reload]);

  React.useEffect(() => {
    if (!data) return;
    if ((!showMessagesTab && tab === 'messages') || (!canEdit && ['settings', 'phone', 'profile', 'email', 'school', 'permissions', 'assistant', 'sports'].includes(tab))) {
      navigate(`/students/${studentId}?tab=today`);
    }
  }, [canEdit, showMessagesTab, tab, data]);

  React.useEffect(() => {
    if (loading || lastNavigationFocus.current === tab) return;
    lastNavigationFocus.current = tab;
  }, [loading, tab]);

  if (loading && !data) return <Loading what="this Student Assistant" />;
  if (error) return <ErrorBanner error={error} onRetry={reload} />;

  const ctx: Ctx = { studentId, reload, data };
  const goToTab = (nextTab: string, anchor?: string) => {
    navigate(`/students/${studentId}?${nextTab === 'setup' ? 'setup=continue' : `tab=${nextTab}`}${anchor ? `#${anchor}` : ''}`);
    const targetId = anchor ?? (nextTab === 'school' ? 'student-school' : nextTab === 'phone' ? 'phone-verification-section' : nextTab === 'class-schedule' ? 'student-class-schedule' : nextTab === 'sports' ? 'student-sports' : nextTab === 'settings' ? 'student-settings' : null);
    if (targetId) {
      window.setTimeout(() => {
        const target = document.getElementById(targetId);
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (target instanceof HTMLElement) {
          target.focus({ preventScroll: true });
          // Briefly highlight the exact section being navigated to and dim its
          // sibling setup sections, so a completed (green) section sitting right
          // next to the actual target (e.g. a not-yet-done yellow section) can't
          // visually steal attention from where the parent actually needs to act.
          target.classList.add('highlight-target');
          const siblings = document.querySelectorAll('.settings-anchor-section');
          siblings.forEach((el) => { if (el !== target) el.classList.add('anchor-dimmed'); });
          window.setTimeout(() => {
            target.classList.remove('highlight-target');
            siblings.forEach((el) => el.classList.remove('anchor-dimmed'));
          }, 2500);
        }
      }, 0);
    }
  };
  const name = data.student.preferred_name;
  const onboarding = initialOnboarding(data.worker);
  const optionalOnboardingComplete = allOptionalOnboardingComplete(data);
  const repair = repairReadiness(data, me);
  const state = setupState(data, me);
  const canvasConnection = data.connections?.find((connection: any) => connection.provider === 'canvas');
  const guidedOnboardingComplete = canEdit && !onboarding && optionalOnboardingComplete;
  const showSetup = canEdit && location.setup;
  const activeCutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const weekStart = startOfCurrentWeek(new Date());
  const nextWeekStart = new Date(weekStart);
  nextWeekStart.setDate(nextWeekStart.getDate() + 7);
  const weekStartMs = weekStart.getTime();
  const nextWeekStartMs = nextWeekStart.getTime();
  const openTasks = (data.tasks ?? [])
    .filter((t: any) => t.status === 'open')
    .filter((t: any) => taskPassesActiveWindow(t, activeCutoff))
    .filter((t: any) => isDueInCurrentWeek(t, weekStartMs, nextWeekStartMs))
    .length;
  const assignmentsStat = state.assignmentSourceReady
    ? `${openTasks} thing${openTasks === 1 ? '' : 's'} to do`
    : canvasConnection?.last_sync_at
      ? `Assignments unavailable · updated ${fmt.ago(canvasConnection.last_sync_at)}`
      : 'Assignments unavailable';
  const canvasNeedsReconnect = canvasConnection?.status === 'needs_reconnection';
  const canvasWaitingForFirstSync = Boolean(
    canvasConnection
    && canvasConnection.status === 'connected'
    && !state.assignmentSourceReady
    && canvasConnection.last_sync_status !== 'failed',
  );
  const canvasWaitingStatus = canvasConnection?.last_sync_status
    ? fmt.label(canvasConnection.last_sync_status)
    : 'Waiting to sync';
  const smsDestination = state.sms?.destination ?? null;
  const pendingReminders = (data.reminders ?? []).filter((r: any) => r.status === 'pending').length;
  const tomorrowCount = data.tomorrow?.occurrences?.filter((o: any) => o.status !== 'canceled').length ?? 0;
  const visibility = VISIBILITY_LABEL[data.student.parent_visibility ?? 'summary'] ?? 'a summary';
  const tabs = [
    { id: 'today', label: 'Overview' },
    { id: 'tasks', label: `Tasks${openTasks ? ` (${openTasks})` : ''}` },
    { id: 'activities', label: 'Activities' },
    { id: 'tools', label: canEdit ? 'Configuration & tools' : 'Tools' },
  ];
  const secondarySection = moreSections.find((section) => section.id === tab);
  const activePrimaryTab = secondarySection ? 'tools' : tab;

  return (
    <>
      <nav className="child-breadcrumb row" aria-label="Child location">
        {canEdit && <button onClick={() => navigate('/')}>Family home</button>}
        <span>{name}</span>
        {data.worker?.status === 'active' && <Pill tone="ok">Active</Pill>}
        <span aria-hidden="true"> / </span>
        <strong id="child-location" tabIndex={-1}>{showSetup ? 'Guided setup' : secondarySection?.label ?? tabs.find((item) => item.id === tab)?.label ?? 'Overview'}</strong>
      </nav>
      {showSetup ? <>
        <h1>{name}'s guided setup</h1>
        <p className="muted">{guidedOnboardingComplete ? 'Setup is complete. You can review saved steps below without changing anything.' : 'Finish the current step below, or return to Family home and resume later.'}</p>
        {!guidedOnboardingComplete && (
          <details className="setup-help-box" id="onboarding-prep-title">
            <summary><strong>Setup prep</strong> <span className="field-help">Have school sites and the student phone nearby if you can.</span></summary>
            <ul>
              <li><strong>Assignments, required for activation:</strong> <a href="https://bush.instructure.com/" target="_blank" rel="noreferrer">Canvas</a></li>
              <li><strong>Class schedule, optional Step 6:</strong> <a href="https://bush.myschoolapp.com/" target="_blank" rel="noreferrer">MySchoolApp (Bush)</a> or your school’s ICS/WebCal portal</li>
              <li><strong>Sports schedules, optional Step 7:</strong> <a href="https://www.arbiterlive.com/" target="_blank" rel="noreferrer">ArbiterLive</a> or your school athletics site</li>
            </ul>
            <p className="field-help">For non-Bush schools, use equivalent Canvas, class-schedule feed, and athletics sources provided by your school.</p>
          </details>
        )}
        <SetupSummary ctx={ctx} me={me} onGoTo={goToTab} onChanged={onChanged} />
      </> : <>
      <Hero
        eyebrow={canEdit ? `${name}'s Student Assistant` : undefined}
        title={canEdit ? `${name}'s Student Assistant` : `${timeGreeting()}, ${name}! 👋`}
        subtitle={canEdit
          ? `${secondarySection?.label ?? tabs.find((item) => item.id === tab)?.label ?? 'Overview'} — changes apply to ${name} only.`
          : "Here's your plan — your assistant keeps track so you don't have to."}
        illustration={<BackpackIllustration title="A packed backpack, ready for the day" decorative={false} />}
        stats={[
          assignmentsStat,
          `${pendingReminders} reminder${pendingReminders === 1 ? '' : 's'} upcoming`,
          `${tomorrowCount} planned tomorrow`,
        ]}
      />

      {tab === 'assistant' && canEdit && <WorkerHeader ctx={ctx} me={me} canEdit={canEdit} onGoTo={goToTab} />}
      {tab === 'today' && <section className="card">
        <h2>Assistant readiness</h2>
        {canEdit && !canvasConnection && (
          <div className="banner" role="status">
            <strong>Connect Canvas assignments</strong>
            <p>Connect Canvas before activation so {name}&apos;s assignments can stay up to date.</p>
            <button className="primary" onClick={() => goToTab('school', 'student-school')}>Connect Canvas</button>
          </div>
        )}
        {canEdit && canvasNeedsReconnect && (
          <div className="banner bad" role="alert">
            <strong>Canvas assignments need attention</strong>
            <p>We could not refresh {name}&apos;s assignments. Reconnect Canvas to keep schoolwork up to date.</p>
            <button className="primary" onClick={() => goToTab('school', 'student-school')}>Reconnect Canvas</button>
          </div>
        )}
        {canEdit && canvasConnection?.status === 'connected' && canvasConnection.last_sync_status === 'failed' && (
          <div className="banner bad" role="alert">
            <strong>Canvas sync failed</strong>
            <p>The latest Canvas sync failed. Open Connect school to reconnect or retry sync.</p>
            <button className="primary" onClick={() => goToTab('school', 'student-school')}>Open Connect school</button>
          </div>
        )}
        {canEdit && canvasWaitingForFirstSync && (
          <div className="banner" role="status">
            <strong>Waiting for assignments</strong>
            <p>Status: {canvasWaitingStatus}{canvasConnection?.last_sync_at ? ` · last updated ${fmt.ago(canvasConnection.last_sync_at)}` : ''}.</p>
          </div>
        )}
        {canEdit && repair.phoneRepairNeeded && (
          <div className="banner bad" role="alert">
            <strong>Repair needed: verify student phone</strong>
            <p>The assistant is enabled, but SMS reminders need a verified, consented, subscribed phone.</p>
            <button className="primary" onClick={() => goToTab('phone', 'phone-verification-section')}>Verify student phone</button>
          </div>
        )}
        {canEdit && onboarding && (
          <div className="banner" role="status">
            <strong>Initial setup is still in progress</strong>
            <p>Required progress is saved. Continue setup to finish permissions, Canvas, and phone verification.</p>
            <button className="primary" onClick={() => goToTab('setup')}>Continue guided setup</button>
          </div>
        )}
        {canEdit && !onboarding && data.readiness?.ready === true && !canvasWaitingForFirstSync && !canvasNeedsReconnect && (
          <p className="muted">Required checks are ready. Optional tools are available in Configuration & tools.</p>
        )}
      </section>}
      {tab === 'today' && <section className="card">
        <h2>Try your assistant</h2>
        {data.smsSimulated ? (
          <p>Messages are simulated in this environment. Send <span className="mono">plan week</span> from the student flow to test responses.</p>
        ) : (
          <>
            <p>
              Assistant destination:{' '}
              {data.smsSenderAddress ? <span className="mono">{data.smsSenderAddress}</span> : 'managed by your messaging service'}.
            </p>
            {state.phoneVerified && smsDestination ? (
              <p>
                From the verified student phone <span className="mono">{smsDestination}</span>, text <span className="mono">plan week</span> to the assistant number above.
              </p>
            ) : (
              <p>
                Verify the student phone first, then send <span className="mono">plan week</span> from that verified number.
              </p>
            )}
          </>
        )}
      </section>}

      <TrustStrip illustration={<ShieldHeartIllustration title="Privacy" decorative={false} />}>
        {canEdit
          ? <><strong>{name}</strong> can see and use this same assistant. Based on the visibility setting in Configure assistant, you can see {visibility}.</>
          : <>Your parent can see <strong>{visibility}</strong>. This is here to help, not to watch — ask your parent to pause the assistant, or text <strong>STOP</strong> to opt out anytime.</>}
      </TrustStrip>

      <SegmentedTabs tabs={tabs} active={activePrimaryTab} onChange={goToTab} />
      {tab === 'tools' && (
        <section className="more-sections" aria-labelledby="more-sections-heading">
          <h2 id="more-sections-heading">{canEdit ? 'Configuration & tools' : 'Tools'}</h2>
          <p className="muted">Choose what you want to set up or manage for {name}. Canvas assignments and class schedules are separate connections.</p>
          <div className="tool-group-list">
            {toolGroups.filter((group) => group.items.length).map((group) => (
              <section className="tool-group" key={group.title} aria-label={group.title}>
                <div className="tool-group-heading">
                  <h3>{group.title}</h3>
                  <p className="muted">{group.description}</p>
                </div>
                <div className="more-sections-grid">
                  {group.items.map((section) => (
                    <button
                      key={section.id}
                      className="more-section-link"
                      type="button"
                      onClick={() => goToTab(section.id)}
                    >
                      <strong>{section.label}</strong>
                      <span className="muted">{section.description}</span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </section>
      )}
      {secondarySection && (
        <button className="more-back" type="button" onClick={() => goToTab('today')}>
          ← Back to {name}'s overview
        </button>
      )}

      <div hidden={tab !== 'today'}>
        <Responsibilities ctx={ctx} canEdit={canEdit} />
        <Reminders ctx={ctx} />
      </div>
      <div hidden={tab !== 'voice'}><VoiceConversation ctx={ctx} /></div>
      <div hidden={tab !== 'tasks'}><Tasks ctx={ctx} /></div>
      <div hidden={tab !== 'activities'}><Activities ctx={ctx} canEdit={canEdit} /></div>
      {canEdit && <div hidden={tab !== 'school'}><School ctx={ctx} /></div>}
      <div hidden={tab !== 'class-schedule'}><ClassSchedule ctx={ctx} canEdit={canEdit} onSetUpLater={() => goToTab('today')} /></div>
      {canEdit && <div hidden={tab !== 'sports'}><SportsSetup ctx={ctx} /></div>}
      <div hidden={tab !== 'messages'}><Messages ctx={ctx} canEdit={canEdit} /></div>
      {canEdit && <>
        <div hidden={tab !== 'profile'}><Profile ctx={ctx} /></div>
        <div hidden={tab !== 'permissions'}><FamilyPermissions me={me} onChanged={onChanged} /></div>
        <div hidden={!['settings', 'phone', 'email'].includes(tab)}><Settings ctx={ctx} canEdit section={tab} /></div>
      </>}
      </>}
    </>
  );
}
